/* ==========================================================================
   Sollero Fashion — Shared Scripts
   File: arScripts.js

   Keep this file cumulative as sections are added.
   Add JavaScript only when a section requires behavior that cannot be handled
   reliably with semantic HTML and CSS alone.
   ========================================================================== */

(() => {
  "use strict";

  const hzHeroHpControllers = new WeakMap();
  const hzHeroHpMobileQuery = window.matchMedia("(max-width: 767px)");
  const hzHeroHpReducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

  function hzHeroHpCreate(root) {
    if (!root || hzHeroHpControllers.has(root)) return;

    const slides = Array.from(root.querySelectorAll("[data-hz-hero-slide]"));
    const motionToggle = root.querySelector("[data-hz-hero-motion-toggle]");
    const motionLabel = root.querySelector("[data-hz-hero-motion-label]");
    const interval = Math.max(3000, Number(root.dataset.hzSlideInterval) || 6000);
    const fadeDuration = Math.max(600, Number(root.dataset.hzFadeDuration) || 1000);

    if (!slides.length) return;

    let currentIndex = Math.max(
      0,
      slides.findIndex((slide) => slide.classList.contains("hz__heroHpSlide--active"))
    );

    let rotationTimerId = null;
    let preloadTimerId = null;
    let transitionFallbackId = null;
    let transitionFrameId = null;
    let userPaused = false;
    let reducedMotionOverride = false;
    let focusPaused = false;
    let destroyed = false;
    let transitioning = false;
    let pendingIndex = null;
    let preparedVideo = null;

    function isMotionPaused() {
      return userPaused || (hzHeroHpReducedMotionQuery.matches && !reducedMotionOverride);
    }

    function isRotationPaused() {
      return isMotionPaused() || focusPaused || document.hidden;
    }

    function getRenderedMedia(slide) {
      if (!slide) return null;

      const hasMobileMedia = slide.classList.contains("hz__heroHpSlide--hasMobileMedia");
      const useMobileMedia = hzHeroHpMobileQuery.matches && hasMobileMedia;
      const selector = useMobileMedia ? ".hz__heroHpMediaMobile" : ".hz__heroHpMediaDesktop";

      return slide.querySelector(selector);
    }

    function getRenderedVideo(slide) {
      return getRenderedMedia(slide)?.querySelector("video") || null;
    }

    function configureVideo(video) {
      if (!video) return;

      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute("muted", "");
      video.setAttribute("playsinline", "");
    }

    function playVideo(video) {
      if (!video || isMotionPaused() || document.hidden) return;

      configureVideo(video);

      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    }

    function pauseVideo(video) {
      if (!video) return;
      video.pause();
    }

    function pauseAllVideosExcept(videosToKeep = []) {
      const keep = new Set(videosToKeep.filter(Boolean));

      root.querySelectorAll("video").forEach((video) => {
        if (!keep.has(video)) {
          pauseVideo(video);
        }
      });
    }

    function clearRotationTimers() {
      if (rotationTimerId !== null) {
        window.clearTimeout(rotationTimerId);
        rotationTimerId = null;
      }

      if (preloadTimerId !== null) {
        window.clearTimeout(preloadTimerId);
        preloadTimerId = null;
      }
    }

    function clearTransitionFallback() {
      if (transitionFallbackId !== null) {
        window.clearTimeout(transitionFallbackId);
        transitionFallbackId = null;
      }
    }

    function clearTransitionFrame() {
      if (transitionFrameId !== null) {
        window.cancelAnimationFrame(transitionFrameId);
        transitionFrameId = null;
      }
    }

    function syncCurrentVideo() {
      const currentVideo = getRenderedVideo(slides[currentIndex]);

      if (isMotionPaused() || document.hidden) {
        pauseAllVideosExcept();
        preparedVideo = null;
        return;
      }

      pauseAllVideosExcept(currentVideo ? [currentVideo] : []);
      playVideo(currentVideo);
      preparedVideo = null;
    }

    function updateMotionToggle() {
      if (!motionToggle) return;

      const controlsSlideshow = slides.length > 1;
      const activeVideo = getRenderedVideo(slides[currentIndex]);
      const shouldShow = controlsSlideshow || Boolean(activeVideo);

      motionToggle.hidden = !shouldShow;

      if (!shouldShow) return;

      const paused = isMotionPaused();
      const subject = controlsSlideshow ? "slideshow" : "video";
      const label = `${paused ? "Play" : "Pause"} ${subject}`;

      motionToggle.classList.toggle("hz__heroHpMotionToggle--paused", paused);
      motionToggle.setAttribute("aria-label", label);
      motionToggle.setAttribute("title", label);

      if (motionLabel) {
        motionLabel.textContent = label;
      }
    }

    function prepareNextVideo(nextIndex) {
      if (
        destroyed ||
        transitioning ||
        isRotationPaused() ||
        !slides[nextIndex] ||
        nextIndex === currentIndex
      ) {
        return;
      }

      const activeVideo = getRenderedVideo(slides[currentIndex]);
      const nextVideo = getRenderedVideo(slides[nextIndex]);

      if (!nextVideo) {
        preparedVideo = null;
        return;
      }

      configureVideo(nextVideo);
      nextVideo.preload = "auto";
      preparedVideo = nextVideo;

      /*
       * Keep the outgoing video alive while the incoming video is warmed.
       * This avoids the frozen-frame/black-flash effect that can happen when
       * the old video is paused before the fade has finished.
       */
      pauseAllVideosExcept([activeVideo, nextVideo]);
      playVideo(activeVideo);
      playVideo(nextVideo);
    }

    function finalizeTransition(previousIndex, nextIndex) {
      if (destroyed || !slides[nextIndex]) return;

      clearTransitionFallback();
      clearTransitionFrame();

      slides.forEach((slide, index) => {
        const isCurrent = index === nextIndex;

        slide.classList.remove(
          "hz__heroHpSlide--transitionTarget",
          "hz__heroHpSlide--leaving"
        );
        slide.classList.toggle("hz__heroHpSlide--active", isCurrent);
        slide.setAttribute("aria-hidden", isCurrent ? "false" : "true");

        if (isCurrent) {
          slide.removeAttribute("inert");
        } else {
          slide.setAttribute("inert", "");
        }
      });

      currentIndex = nextIndex;
      transitioning = false;

      const currentVideo = getRenderedVideo(slides[currentIndex]);

      if (isMotionPaused() || document.hidden) {
        pauseAllVideosExcept();
      } else {
        pauseAllVideosExcept(currentVideo ? [currentVideo] : []);
        playVideo(currentVideo);
      }

      preparedVideo = null;
      updateMotionToggle();

      if (pendingIndex !== null && pendingIndex !== currentIndex) {
        const queuedIndex = pendingIndex;
        pendingIndex = null;
        changeSlide(queuedIndex);
        return;
      }

      pendingIndex = null;
      scheduleNext();
    }

    function changeSlide(nextIndex) {
      if (destroyed || !slides[nextIndex] || nextIndex === currentIndex) return;

      if (transitioning) {
        pendingIndex = nextIndex;
        return;
      }

      clearRotationTimers();

      const previousIndex = currentIndex;
      const outgoingSlide = slides[previousIndex];
      const incomingSlide = slides[nextIndex];

      if (hzHeroHpReducedMotionQuery.matches && !reducedMotionOverride) {
        currentIndex = nextIndex;

        slides.forEach((slide, index) => {
          const isCurrent = index === nextIndex;

          slide.classList.remove(
            "hz__heroHpSlide--transitionTarget",
            "hz__heroHpSlide--leaving"
          );
          slide.classList.toggle("hz__heroHpSlide--active", isCurrent);
          slide.setAttribute("aria-hidden", isCurrent ? "false" : "true");

          if (isCurrent) {
            slide.removeAttribute("inert");
          } else {
            slide.setAttribute("inert", "");
          }
        });

        syncCurrentVideo();
        updateMotionToggle();
        scheduleNext();
        return;
      }

      transitioning = true;

      const outgoingVideo = getRenderedVideo(outgoingSlide);
      const incomingVideo = getRenderedVideo(incomingSlide);

      /*
       * Make the target renderable at opacity 0 first. The next animation
       * frame then flips opacity on both layers, guaranteeing a real
       * crossfade rather than relying on a browser to notice two class
       * mutations in the same paint.
       */
      incomingSlide.classList.add("hz__heroHpSlide--transitionTarget");
      incomingSlide.setAttribute("aria-hidden", "false");
      incomingSlide.removeAttribute("inert");

      outgoingSlide.setAttribute("aria-hidden", "true");
      outgoingSlide.setAttribute("inert", "");

      if (!isMotionPaused() && !document.hidden) {
        pauseAllVideosExcept([outgoingVideo, incomingVideo]);
        playVideo(outgoingVideo);

        if (incomingVideo) {
          incomingVideo.preload = "auto";
          playVideo(incomingVideo);
        }
      } else {
        pauseAllVideosExcept();
      }

      /*
       * Force the target's opacity:0 state to be committed before the next
       * frame. This removes the intermittent "cut instead of fade" seen when
       * browsers batch the visibility and opacity changes together.
       */
      incomingSlide.getBoundingClientRect();

      transitionFrameId = window.requestAnimationFrame(() => {
        transitionFrameId = null;

        if (destroyed) return;

        outgoingSlide.classList.remove("hz__heroHpSlide--active");
        outgoingSlide.classList.add("hz__heroHpSlide--leaving");

        incomingSlide.classList.add("hz__heroHpSlide--active");

        const handleTransitionEnd = (event) => {
          if (event.target !== incomingSlide || event.propertyName !== "opacity") return;

          incomingSlide.removeEventListener("transitionend", handleTransitionEnd);
          finalizeTransition(previousIndex, nextIndex);
        };

        incomingSlide.addEventListener("transitionend", handleTransitionEnd);

        /*
         * transitionend can be skipped in rare browser/theme-editor state
         * changes. The fallback makes the controller deterministic.
         */
        transitionFallbackId = window.setTimeout(() => {
          incomingSlide.removeEventListener("transitionend", handleTransitionEnd);
          finalizeTransition(previousIndex, nextIndex);
        }, fadeDuration + 160);
      });
    }

    function scheduleNext() {
      clearRotationTimers();

      if (
        destroyed ||
        transitioning ||
        slides.length < 2 ||
        isRotationPaused()
      ) {
        return;
      }

      const nextIndex = (currentIndex + 1) % slides.length;
      const warmLead = Math.min(900, Math.max(450, Math.round(interval * 0.15)));
      const warmDelay = Math.max(0, interval - warmLead);

      preloadTimerId = window.setTimeout(() => {
        preloadTimerId = null;
        prepareNextVideo(nextIndex);
      }, warmDelay);

      rotationTimerId = window.setTimeout(() => {
        rotationTimerId = null;
        changeSlide(nextIndex);
      }, interval);
    }

    function handleMotionToggle() {
      if (hzHeroHpReducedMotionQuery.matches && !reducedMotionOverride) {
        reducedMotionOverride = true;
        userPaused = false;
      } else {
        userPaused = !userPaused;
      }

      updateMotionToggle();

      if (isMotionPaused()) {
        clearRotationTimers();
        pauseAllVideosExcept();
        preparedVideo = null;
        return;
      }

      syncCurrentVideo();
      scheduleNext();
    }

    function handleFocusIn(event) {
      /*
       * Pause only while a CTA is keyboard-focused. The old implementation
       * paused whenever anything in the hero received focus, including the
       * pause/play control itself, which made resume behavior feel random.
       */
      if (!event.target.closest?.(".hz__heroHpCta")) return;

      focusPaused = true;
      clearRotationTimers();
    }

    function handleFocusOut(event) {
      if (!focusPaused) return;

      const nextTarget = event.relatedTarget;
      if (nextTarget?.closest?.(".hz__heroHpCta") && root.contains(nextTarget)) {
        return;
      }

      focusPaused = false;
      scheduleNext();
    }

    function handleVisibilityChange() {
      if (document.hidden) {
        clearRotationTimers();
        pauseAllVideosExcept();
        preparedVideo = null;
        return;
      }

      syncCurrentVideo();
      updateMotionToggle();
      scheduleNext();
    }

    function handleBreakpointChange() {
      clearRotationTimers();
      preparedVideo = null;
      syncCurrentVideo();
      updateMotionToggle();
      scheduleNext();
    }

    function handleReducedMotionChange(event) {
      if (event.matches) {
        reducedMotionOverride = false;
      }

      clearRotationTimers();
      updateMotionToggle();
      syncCurrentVideo();
      scheduleNext();
    }

    function selectBlock(blockId) {
      const nextIndex = slides.findIndex((slide) => slide.dataset.hzHeroBlockId === blockId);
      if (nextIndex < 0) return;

      userPaused = true;
      reducedMotionOverride = false;
      clearRotationTimers();

      if (nextIndex === currentIndex) {
        syncCurrentVideo();
        updateMotionToggle();
        return;
      }

      changeSlide(nextIndex);
      updateMotionToggle();
    }

    function destroy() {
      destroyed = true;
      clearRotationTimers();
      clearTransitionFallback();
      clearTransitionFrame();
      pauseAllVideosExcept();

      root.removeEventListener("focusin", handleFocusIn);
      root.removeEventListener("focusout", handleFocusOut);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      hzHeroHpMobileQuery.removeEventListener("change", handleBreakpointChange);
      hzHeroHpReducedMotionQuery.removeEventListener("change", handleReducedMotionChange);

      if (motionToggle) {
        motionToggle.removeEventListener("click", handleMotionToggle);
      }

      hzHeroHpControllers.delete(root);
    }

    root.addEventListener("focusin", handleFocusIn);
    root.addEventListener("focusout", handleFocusOut);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    hzHeroHpMobileQuery.addEventListener("change", handleBreakpointChange);
    hzHeroHpReducedMotionQuery.addEventListener("change", handleReducedMotionChange);

    if (motionToggle) {
      motionToggle.addEventListener("click", handleMotionToggle);
    }

    hzHeroHpControllers.set(root, { destroy, selectBlock });

    updateMotionToggle();
    syncCurrentVideo();
    scheduleNext();
  }

  function hzHeroHpInitWithin(scope = document) {
    if (scope.matches && scope.matches("[data-hz-hero-hp]")) {
      hzHeroHpCreate(scope);
    }

    scope.querySelectorAll?.("[data-hz-hero-hp]").forEach(hzHeroHpCreate);
  }

  function hzHeroHpDestroyWithin(scope) {
    const roots = [];

    if (scope.matches && scope.matches("[data-hz-hero-hp]")) {
      roots.push(scope);
    }

    scope.querySelectorAll?.("[data-hz-hero-hp]").forEach((root) => roots.push(root));

    roots.forEach((root) => {
      hzHeroHpControllers.get(root)?.destroy();
    });
  }

  function hzHeroHpHandleBlockSelect(event) {
    const slide = event.target.closest?.("[data-hz-hero-slide]");
    const root = slide?.closest?.("[data-hz-hero-hp]");
    if (!slide || !root) return;

    hzHeroHpControllers.get(root)?.selectBlock(slide.dataset.hzHeroBlockId);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => hzHeroHpInitWithin(document), { once: true });
  } else {
    hzHeroHpInitWithin(document);
  }

  document.addEventListener("shopify:section:load", (event) => hzHeroHpInitWithin(event.target));
  document.addEventListener("shopify:section:unload", (event) => hzHeroHpDestroyWithin(event.target));
  document.addEventListener("shopify:block:select", hzHeroHpHandleBlockSelect);
})();

(() => {
  "use strict";

  const hzCollectionsHpControllers = new WeakMap();
  const hzCollectionsHpMobileQuery = window.matchMedia("(max-width: 767px)");
  const hzCollectionsHpReducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

  function hzCollectionsHpCreate(root) {
    if (!root || hzCollectionsHpControllers.has(root)) return;

    const gallery = root.querySelector("[data-hz-collections-slider]");
    const slides = Array.from(root.querySelectorAll("[data-hz-collections-slide]"));
    const dots = Array.from(root.querySelectorAll("[data-hz-collections-dot]"));

    if (!gallery || !slides.length) return;

    const sliderBehavior = gallery.dataset.hzCollectionsSliderBehavior === "loop" ? "loop" : "bounded";
    let cloneBeforeSlides = [];
    let cloneAfterSlides = [];
    let sequenceSlides = slides.slice();
    let activeIndex = Math.max(
      0,
      slides.findIndex((slide) => slide.classList.contains("hz__collectionsHpCard--active"))
    );
    let frameId = null;
    let settleTimerId = null;
    let destroyed = false;
    let isJumping = false;

    slides.forEach((slide, index) => {
      slide.dataset.hzCollectionsLogicalIndex = String(index);
    });

    function sanitizeClone(source, logicalIndex) {
      const clone = source.cloneNode(true);

      clone.classList.add("hz__collectionsHpCard--clone");
      clone.classList.remove("hz__collectionsHpCard--active");
      clone.setAttribute("data-hz-collections-clone", "");
      clone.setAttribute("data-hz-collections-logical-index", String(logicalIndex));
      clone.setAttribute("aria-hidden", "true");
      clone.setAttribute("inert", "");
      clone.removeAttribute("data-hz-collections-block-id");

      [clone, ...clone.querySelectorAll("*")].forEach((element) => {
        Array.from(element.attributes).forEach((attribute) => {
          if (attribute.name === "id" || attribute.name.startsWith("data-shopify")) {
            element.removeAttribute(attribute.name);
          }
        });
      });

      return clone;
    }

    if (sliderBehavior === "loop" && slides.length > 1) {
      /*
       * Keep two logical neighbors on both loop edges.
       *
       * With only a single edge clone, the tail looked like:
       *   ... Last -> cloned First -> [nothing]
       *
       * After normalization it became:
       *   cloned Last -> real First -> real Second
       *
       * That made Second appear in a single frame. A two-slide buffer keeps
       * the neighborhood visually equivalent before and after the invisible
       * clone -> real handoff.
       */
      const cloneBufferSize = Math.min(2, slides.length);
      const beforeFragment = document.createDocumentFragment();
      const afterFragment = document.createDocumentFragment();

      for (let offset = cloneBufferSize; offset >= 1; offset -= 1) {
        const logicalIndex = slides.length - offset;
        const clone = sanitizeClone(slides[logicalIndex], logicalIndex);

        cloneBeforeSlides.push(clone);
        beforeFragment.appendChild(clone);
      }

      for (let logicalIndex = 0; logicalIndex < cloneBufferSize; logicalIndex += 1) {
        const clone = sanitizeClone(slides[logicalIndex], logicalIndex);

        cloneAfterSlides.push(clone);
        afterFragment.appendChild(clone);
      }

      gallery.insertBefore(beforeFragment, slides[0]);
      gallery.appendChild(afterFragment);

      sequenceSlides = Array.from(gallery.querySelectorAll("[data-hz-collections-slide]"));
    }

    function clearSettleTimer() {
      if (settleTimerId !== null) {
        window.clearTimeout(settleTimerId);
        settleTimerId = null;
      }
    }

    function updateActive(nextIndex) {
      if (destroyed || !slides[nextIndex] || nextIndex === activeIndex) return;

      slides.forEach((slide, index) => {
        slide.classList.toggle("hz__collectionsHpCard--active", index === nextIndex);
      });

      dots.forEach((dot, index) => {
        const isActive = index === nextIndex;
        dot.classList.toggle("hz__collectionsHpDot--active", isActive);
        dot.tabIndex = isActive ? 0 : -1;

        if (isActive) {
          dot.setAttribute("aria-current", "true");
        } else {
          dot.removeAttribute("aria-current");
        }
      });

      activeIndex = nextIndex;
    }

    function getLogicalIndex(slide) {
      const index = Number(slide?.dataset.hzCollectionsLogicalIndex);
      return Number.isInteger(index) ? index : -1;
    }

    function getClosestSequenceSlide() {
      const galleryRect = gallery.getBoundingClientRect();
      const galleryCenter = galleryRect.left + galleryRect.width / 2;
      let closestSlide = null;
      let closestDistance = Number.POSITIVE_INFINITY;

      sequenceSlides.forEach((slide) => {
        const slideRect = slide.getBoundingClientRect();
        if (slideRect.width === 0) return;

        const slideCenter = slideRect.left + slideRect.width / 2;
        const distance = Math.abs(galleryCenter - slideCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestSlide = slide;
        }
      });

      return closestSlide;
    }

    function updateVisualHeights() {
      if (!hzCollectionsHpMobileQuery.matches) {
        sequenceSlides.forEach((slide) => {
          slide.style.removeProperty("--hz-collections-hp-card-height");
        });
        return;
      }

      const galleryRect = gallery.getBoundingClientRect();
      const galleryCenter = galleryRect.left + galleryRect.width / 2;
      const gap = Number.parseFloat(window.getComputedStyle(gallery).columnGap) || 10;
      const closestSlide = hzCollectionsHpReducedMotionQuery.matches ? getClosestSequenceSlide() : null;

      sequenceSlides.forEach((slide) => {
        const slideRect = slide.getBoundingClientRect();
        if (slideRect.width === 0) return;

        let height = 308;

        if (hzCollectionsHpReducedMotionQuery.matches) {
          if (slide === closestSlide) {
            height = 344;
          }
        } else {
          const slideCenter = slideRect.left + slideRect.width / 2;
          const distance = Math.abs(galleryCenter - slideCenter);
          const activationDistance = Math.max(slideRect.width + gap, 1);
          const progress = Math.max(0, Math.min(1, 1 - distance / activationDistance));

          height = 308 + 36 * progress;
        }

        slide.style.setProperty("--hz-collections-hp-card-height", `${height.toFixed(2)}px`);
      });
    }

    function syncFromPosition() {
      frameId = null;
      if (destroyed || !hzCollectionsHpMobileQuery.matches) return;

      updateVisualHeights();

      const closestSlide = getClosestSequenceSlide();
      const logicalIndex = getLogicalIndex(closestSlide);

      if (logicalIndex >= 0) {
        updateActive(logicalIndex);
      }
    }

    function schedulePositionSync() {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(syncFromPosition);
    }

    function jumpToElement(element, visualSource = null) {
      if (!element || !hzCollectionsHpMobileQuery.matches) return;

      isJumping = true;
      clearSettleTimer();

      const previousSnapType = gallery.style.scrollSnapType;
      const previousScrollBehavior = gallery.style.scrollBehavior;
      const targetSurface = element.querySelector(".hz__collectionsHpCardSurface");
      const sourceSurface = visualSource?.querySelector(".hz__collectionsHpCardSurface");
      let mirroredHeight = null;

      /*
       * A loop edge first lands on a visual clone and then silently teleports
       * to its real counterpart. Preserve the clone's exact rendered height
       * across that hidden handoff so there is no 344 -> 308 -> 344 flash.
       */
      if (targetSurface && sourceSurface) {
        const sourceHeight = sourceSurface.getBoundingClientRect().height;

        if (Number.isFinite(sourceHeight) && sourceHeight > 0) {
          mirroredHeight = `${sourceHeight.toFixed(2)}px`;
          element.classList.add("hz__collectionsHpCard--normalizing");
          element.style.setProperty("--hz-collections-hp-card-height", mirroredHeight);
          targetSurface.getBoundingClientRect();
        }
      }

      gallery.style.scrollSnapType = "none";
      gallery.style.scrollBehavior = "auto";

      element.scrollIntoView({
        behavior: "auto",
        block: "nearest",
        inline: "center"
      });

      /*
       * scrollIntoView({ behavior: "auto" }) updates the scroll position
       * synchronously. Recalculate the whole visible neighborhood NOW, in the
       * same task, so adjacent cards (especially Second after First) already
       * have their correct interpolated height before the browser can paint
       * the normalized position.
       */
      updateVisualHeights();
      targetSurface?.getBoundingClientRect();

      window.requestAnimationFrame(() => {
        if (mirroredHeight !== null) {
          element.classList.remove("hz__collectionsHpCard--normalizing");
        }

        gallery.style.scrollSnapType = previousSnapType;
        gallery.style.scrollBehavior = previousScrollBehavior;
        isJumping = false;
        schedulePositionSync();
      });
    }

    function getBestSequenceTarget(logicalIndex) {
      if (sliderBehavior !== "loop" || !hzCollectionsHpMobileQuery.matches) {
        return slides[logicalIndex];
      }

      const galleryRect = gallery.getBoundingClientRect();
      const galleryCenter = galleryRect.left + galleryRect.width / 2;
      let bestTarget = slides[logicalIndex];
      let bestDistance = Number.POSITIVE_INFINITY;

      sequenceSlides.forEach((slide) => {
        if (getLogicalIndex(slide) !== logicalIndex) return;

        const slideRect = slide.getBoundingClientRect();
        if (slideRect.width === 0) return;

        const slideCenter = slideRect.left + slideRect.width / 2;
        const distance = Math.abs(galleryCenter - slideCenter);

        if (distance < bestDistance) {
          bestDistance = distance;
          bestTarget = slide;
        }
      });

      return bestTarget;
    }

    function normalizeLoopPosition() {
      if (
        destroyed ||
        isJumping ||
        sliderBehavior !== "loop" ||
        !hzCollectionsHpMobileQuery.matches
      ) {
        return;
      }

      const closestSlide = getClosestSequenceSlide();
      if (!closestSlide?.hasAttribute("data-hz-collections-clone")) return;

      const logicalIndex = getLogicalIndex(closestSlide);
      if (logicalIndex < 0 || !slides[logicalIndex]) return;

      jumpToElement(slides[logicalIndex], closestSlide);
    }

    function scrollToSlide(index, focusDot = false) {
      if (!slides[index]) return;

      updateActive(index);

      if (hzCollectionsHpMobileQuery.matches) {
        const target = getBestSequenceTarget(index);

        target?.scrollIntoView({
          behavior: hzCollectionsHpReducedMotionQuery.matches ? "auto" : "smooth",
          block: "nearest",
          inline: "center"
        });

        schedulePositionSync();
      } else {
        slides[index].scrollIntoView({
          behavior: "auto",
          block: "nearest",
          inline: "nearest"
        });
      }

      if (focusDot && dots[index]) {
        dots[index].focus({ preventScroll: true });
      }
    }

    function handleScroll() {
      if (isJumping) return;

      schedulePositionSync();
      clearSettleTimer();

      settleTimerId = window.setTimeout(() => {
        settleTimerId = null;
        normalizeLoopPosition();
      }, 120);
    }

    function handleDotClick(event) {
      const dot = event.currentTarget;
      const index = Number(dot.dataset.hzCollectionsDotIndex);
      if (!Number.isInteger(index)) return;

      scrollToSlide(index);
    }

    function handleDotKeydown(event) {
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;

      const currentDot = event.currentTarget;
      const currentIndex = Number(currentDot.dataset.hzCollectionsDotIndex);
      if (!Number.isInteger(currentIndex)) return;

      const isRtl = window.getComputedStyle(root).direction === "rtl";
      let nextIndex = currentIndex;

      if (event.key === "Home") {
        nextIndex = 0;
      } else if (event.key === "End") {
        nextIndex = slides.length - 1;
      } else {
        const visualDelta = event.key === "ArrowRight" ? 1 : -1;
        const logicalDelta = isRtl ? visualDelta * -1 : visualDelta;
        nextIndex = (currentIndex + logicalDelta + slides.length) % slides.length;
      }

      event.preventDefault();
      scrollToSlide(nextIndex, true);
    }

    function handleFocusIn(event) {
      if (!hzCollectionsHpMobileQuery.matches) return;

      const slide = event.target.closest?.("[data-hz-collections-slide]");
      if (!slide || !gallery.contains(slide) || slide.hasAttribute("data-hz-collections-clone")) return;

      const index = getLogicalIndex(slide);
      if (index < 0) return;

      scrollToSlide(index);
    }

    function handleBreakpointChange(event) {
      clearSettleTimer();

      if (event.matches) {
        window.requestAnimationFrame(() => {
          if (sliderBehavior === "loop" && slides[activeIndex]) {
            jumpToElement(slides[activeIndex]);
          } else {
            schedulePositionSync();
          }
        });
      } else {
        sequenceSlides.forEach((slide) => {
          slide.style.removeProperty("--hz-collections-hp-card-height");
        });
      }
    }

    function handleResize() {
      if (!hzCollectionsHpMobileQuery.matches) return;
      schedulePositionSync();
    }

    function handleReducedMotionChange() {
      schedulePositionSync();
    }

    function selectSlide(slide) {
      if (!slide || slide.hasAttribute("data-hz-collections-clone")) return;

      const index = getLogicalIndex(slide);
      if (index < 0) return;

      scrollToSlide(index);
    }

    function destroy() {
      destroyed = true;
      clearSettleTimer();

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
        frameId = null;
      }

      gallery.removeEventListener("scroll", handleScroll);
      gallery.removeEventListener("focusin", handleFocusIn);
      window.removeEventListener("resize", handleResize);
      hzCollectionsHpMobileQuery.removeEventListener("change", handleBreakpointChange);
      hzCollectionsHpReducedMotionQuery.removeEventListener("change", handleReducedMotionChange);

      dots.forEach((dot) => {
        dot.removeEventListener("click", handleDotClick);
        dot.removeEventListener("keydown", handleDotKeydown);
      });

      cloneBeforeSlides.forEach((clone) => clone.remove());
      cloneAfterSlides.forEach((clone) => clone.remove());
      cloneBeforeSlides = [];
      cloneAfterSlides = [];

      slides.forEach((slide) => {
        slide.style.removeProperty("--hz-collections-hp-card-height");
        delete slide.dataset.hzCollectionsLogicalIndex;
      });

      hzCollectionsHpControllers.delete(root);
    }

    gallery.addEventListener("scroll", handleScroll, { passive: true });
    gallery.addEventListener("focusin", handleFocusIn);
    window.addEventListener("resize", handleResize, { passive: true });
    hzCollectionsHpMobileQuery.addEventListener("change", handleBreakpointChange);
    hzCollectionsHpReducedMotionQuery.addEventListener("change", handleReducedMotionChange);

    dots.forEach((dot) => {
      dot.addEventListener("click", handleDotClick);
      dot.addEventListener("keydown", handleDotKeydown);
    });

    hzCollectionsHpControllers.set(root, { destroy, selectSlide });

    if (hzCollectionsHpMobileQuery.matches) {
      window.requestAnimationFrame(() => {
        if (sliderBehavior === "loop" && slides[activeIndex]) {
          jumpToElement(slides[activeIndex]);
        } else {
          schedulePositionSync();
        }
      });
    }
  }

  function hzCollectionsHpInitWithin(scope = document) {
    if (scope.matches && scope.matches("[data-hz-collections-hp]")) {
      hzCollectionsHpCreate(scope);
    }

    scope.querySelectorAll?.("[data-hz-collections-hp]").forEach(hzCollectionsHpCreate);
  }

  function hzCollectionsHpDestroyWithin(scope) {
    const roots = [];

    if (scope.matches && scope.matches("[data-hz-collections-hp]")) {
      roots.push(scope);
    }

    scope.querySelectorAll?.("[data-hz-collections-hp]").forEach((root) => roots.push(root));

    roots.forEach((root) => {
      hzCollectionsHpControllers.get(root)?.destroy();
    });
  }

  function hzCollectionsHpHandleBlockSelect(event) {
    const slide = event.target.closest?.("[data-hz-collections-slide]");
    const root = slide?.closest?.("[data-hz-collections-hp]");
    if (!slide || !root) return;

    hzCollectionsHpControllers.get(root)?.selectSlide(slide);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => hzCollectionsHpInitWithin(document), { once: true });
  } else {
    hzCollectionsHpInitWithin(document);
  }

  document.addEventListener("shopify:section:load", (event) => hzCollectionsHpInitWithin(event.target));
  document.addEventListener("shopify:section:unload", (event) => hzCollectionsHpDestroyWithin(event.target));
  document.addEventListener("shopify:block:select", hzCollectionsHpHandleBlockSelect);
})();

/* --------------------------------------------------------------------------
   AR Lifestyle Carousel HP
   Continuous native-scroll carousel with fixed center geometry.
   -------------------------------------------------------------------------- */

(() => {
  "use strict";

  const hzLifestyleCarouselHpControllers = new WeakMap();
  const hzLifestyleCarouselHpMobileQuery = window.matchMedia("(max-width: 767px)");
  const hzLifestyleCarouselHpReducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

  function hzLifestyleCarouselHpCreate(root) {
    if (!root || hzLifestyleCarouselHpControllers.has(root)) return;

    const track = root.querySelector("[data-hz-lifestyle-track]");
    const realSlides = Array.from(root.querySelectorAll("[data-hz-lifestyle-slide]"));
    const previousButton = root.querySelector("[data-hz-lifestyle-previous]");
    const nextButton = root.querySelector("[data-hz-lifestyle-next]");
    const progress = root.querySelector("[data-hz-lifestyle-progress]");
    const progressIndicator = root.querySelector("[data-hz-lifestyle-progress-indicator]");

    if (!track || !realSlides.length) return;

    const cloneBufferSize = realSlides.length > 1 ? 5 : 0;
    let beforeClones = [];
    let afterClones = [];
    let sequenceSlides = realSlides.slice();
    let activeIndex = 0;
    let frameId = null;
    let settleTimerId = null;
    let resizeTimerId = null;
    let destroyed = false;
    let isJumping = false;
    let initialStateReleased = false;

    realSlides.forEach((slide, index) => {
      slide.dataset.hzLifestyleLogicalIndex = String(index);
    });

    function sanitizeClone(source, logicalIndex) {
      const clone = source.cloneNode(true);

      clone.classList.add("hz__lifestyleCarouselHpSlide--clone");
      clone.classList.remove("hz__lifestyleCarouselHpSlide--initial");
      clone.setAttribute("data-hz-lifestyle-clone", "");
      clone.setAttribute("data-hz-lifestyle-logical-index", String(logicalIndex));
      clone.setAttribute("aria-hidden", "true");
      clone.setAttribute("inert", "");
      clone.setAttribute("role", "presentation");
      clone.removeAttribute("aria-current");
      clone.removeAttribute("data-hz-lifestyle-block-id");

      [clone, ...clone.querySelectorAll("*")].forEach((element) => {
        Array.from(element.attributes).forEach((attribute) => {
          if (attribute.name === "id" || attribute.name.startsWith("data-shopify")) {
            element.removeAttribute(attribute.name);
          }
        });
      });

      return clone;
    }

    if (cloneBufferSize > 0) {
      const beforeFragment = document.createDocumentFragment();
      const afterFragment = document.createDocumentFragment();

      for (let offset = cloneBufferSize; offset >= 1; offset -= 1) {
        const logicalIndex = (realSlides.length - (offset % realSlides.length)) % realSlides.length;
        const clone = sanitizeClone(realSlides[logicalIndex], logicalIndex);

        beforeClones.push(clone);
        beforeFragment.appendChild(clone);
      }

      for (let offset = 0; offset < cloneBufferSize; offset += 1) {
        const logicalIndex = offset % realSlides.length;
        const clone = sanitizeClone(realSlides[logicalIndex], logicalIndex);

        afterClones.push(clone);
        afterFragment.appendChild(clone);
      }

      track.insertBefore(beforeFragment, realSlides[0]);
      track.appendChild(afterFragment);
      sequenceSlides = Array.from(track.querySelectorAll("[data-hz-lifestyle-slide]"));
    }

    function clearSettleTimer() {
      if (settleTimerId !== null) {
        window.clearTimeout(settleTimerId);
        settleTimerId = null;
      }
    }

    function clearResizeTimer() {
      if (resizeTimerId !== null) {
        window.clearTimeout(resizeTimerId);
        resizeTimerId = null;
      }
    }

    function getLogicalIndex(slide) {
      const index = Number(slide?.dataset.hzLifestyleLogicalIndex);
      return Number.isInteger(index) ? index : -1;
    }

    function getGap() {
      const styles = window.getComputedStyle(track);
      return Number.parseFloat(styles.columnGap || styles.gap) || 16;
    }

    function getClosestSequenceSlide() {
      const trackRect = track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;
      let closest = null;
      let closestDistance = Number.POSITIVE_INFINITY;

      sequenceSlides.forEach((slide) => {
        const rect = slide.getBoundingClientRect();
        if (rect.width <= 0) return;

        const center = rect.left + rect.width / 2;
        const distance = Math.abs(trackCenter - center);

        if (distance < closestDistance) {
          closestDistance = distance;
          closest = slide;
        }
      });

      return closest;
    }

    function getGeometry() {
      const viewportWidth = window.innerWidth;
      const referenceSlide = realSlides[0];
      const itemWidth = referenceSlide?.getBoundingClientRect().width || 232;

      if (viewportWidth <= 767) {
        return {
          itemWidth,
          inactiveWidth: 212,
          activeWidth: 252,
          inactiveHeight: 268.7944641113281,
          activeHeight: 331
        };
      }

      if (viewportWidth < 1024) {
        const progress = Math.max(0, Math.min(1, (viewportWidth - 768) / 256));
        const desktopInactiveHeight = 252 * (549 / 434);
        const desktopActiveHeight = desktopInactiveHeight + 76;

        return {
          itemWidth,
          inactiveWidth: 212 + (252 - 212) * progress,
          activeWidth: 252,
          inactiveHeight: 268.7944641113281 + (desktopInactiveHeight - 268.7944641113281) * progress,
          activeHeight: 331 + (desktopActiveHeight - 331) * progress
        };
      }

      const inactiveHeight = itemWidth * (549 / 434);

      return {
        itemWidth,
        inactiveWidth: itemWidth,
        activeWidth: itemWidth,
        inactiveHeight,
        activeHeight: inactiveHeight + 76
      };
    }

    function setProgress(nextIndex) {
      if (!progress || !progressIndicator) return;

      const count = realSlides.length;
      const current = Math.max(1, Math.min(count, nextIndex + 1));
      const fill = count > 0 ? current / count : 0;

      progress.setAttribute("aria-valuenow", String(current));
      progress.setAttribute("aria-valuetext", `Slide ${current} of ${count}`);
      progressIndicator.style.transform = `scaleX(${fill.toFixed(6)})`;
    }

    function setActive(nextIndex) {
      if (!realSlides[nextIndex]) return;

      if (nextIndex !== activeIndex) {
        realSlides.forEach((slide, index) => {
          if (index === nextIndex) {
            slide.setAttribute("aria-current", "true");
          } else {
            slide.removeAttribute("aria-current");
          }
        });

        activeIndex = nextIndex;
      }

      setProgress(activeIndex);
    }

    function updateVisuals() {
      if (destroyed) return;

      if (!initialStateReleased) {
        realSlides.forEach((slide) => {
          slide.classList.remove("hz__lifestyleCarouselHpSlide--initial");
        });
        initialStateReleased = true;
      }

      const geometry = getGeometry();
      const gap = getGap();
      const trackRect = track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;
      const activationDistance = Math.max(geometry.itemWidth + gap, 1);

      root.style.setProperty(
        "--hz-lifestyle-carousel-stage-height",
        `${geometry.activeHeight.toFixed(3)}px`
      );

      let closest = null;
      let closestDistance = Number.POSITIVE_INFINITY;

      sequenceSlides.forEach((slide) => {
        const rect = slide.getBoundingClientRect();
        if (rect.width <= 0) return;

        const center = rect.left + rect.width / 2;
        const distance = Math.abs(trackCenter - center);
        const centerProgress = Math.max(
          0,
          Math.min(1, 1 - distance / activationDistance)
        );

        const surfaceWidth =
          geometry.inactiveWidth +
          (geometry.activeWidth - geometry.inactiveWidth) * centerProgress;

        const surfaceHeight =
          geometry.inactiveHeight +
          (geometry.activeHeight - geometry.inactiveHeight) * centerProgress;

        slide.style.setProperty(
          "--hz-lifestyle-carousel-surface-width",
          `${surfaceWidth.toFixed(3)}px`
        );
        slide.style.setProperty(
          "--hz-lifestyle-carousel-surface-height",
          `${surfaceHeight.toFixed(3)}px`
        );

        if (distance < closestDistance) {
          closestDistance = distance;
          closest = slide;
        }
      });

      const logicalIndex = getLogicalIndex(closest);
      if (logicalIndex >= 0) {
        setActive(logicalIndex);
      }
    }

    function syncFromPosition() {
      frameId = null;
      updateVisuals();
    }

    function schedulePositionSync() {
      if (frameId !== null || destroyed) return;
      frameId = window.requestAnimationFrame(syncFromPosition);
    }

    function jumpToRealSlide(logicalIndex, sourceSlide = null) {
      const target = realSlides[logicalIndex];
      if (!target || destroyed) return;

      isJumping = true;
      clearSettleTimer();
      root.classList.add("hz__lifestyleCarouselHp--normalizing");

      const previousSnapType = track.style.scrollSnapType;
      const previousBehavior = track.style.scrollBehavior;

      track.style.scrollSnapType = "none";
      track.style.scrollBehavior = "auto";

      if (sourceSlide) {
        const sourceWidth = sourceSlide.style.getPropertyValue(
          "--hz-lifestyle-carousel-surface-width"
        );
        const sourceHeight = sourceSlide.style.getPropertyValue(
          "--hz-lifestyle-carousel-surface-height"
        );

        if (sourceWidth) {
          target.style.setProperty(
            "--hz-lifestyle-carousel-surface-width",
            sourceWidth
          );
        }

        if (sourceHeight) {
          target.style.setProperty(
            "--hz-lifestyle-carousel-surface-height",
            sourceHeight
          );
        }
      }

      target.scrollIntoView({
        behavior: "auto",
        block: "nearest",
        inline: "center"
      });

      /*
       * Recalculate all visual widths/heights in the same task as the
       * invisible clone -> real teleport. This ensures the browser cannot
       * paint a stale neighbor or stale active-card size for one frame.
       */
      updateVisuals();
      target.getBoundingClientRect();

      window.requestAnimationFrame(() => {
        if (destroyed) return;

        track.style.scrollSnapType = previousSnapType;
        track.style.scrollBehavior = previousBehavior;
        root.classList.remove("hz__lifestyleCarouselHp--normalizing");
        isJumping = false;
        schedulePositionSync();
      });
    }

    function normalizeLoopPosition() {
      if (destroyed || isJumping || cloneBufferSize === 0) return;

      const closest = getClosestSequenceSlide();
      if (!closest?.hasAttribute("data-hz-lifestyle-clone")) return;

      const logicalIndex = getLogicalIndex(closest);
      if (logicalIndex < 0) return;

      jumpToRealSlide(logicalIndex, closest);
    }

    function navigate(direction) {
      if (destroyed || realSlides.length <= 1 || isJumping) return;

      const closest = getClosestSequenceSlide();
      if (!closest) return;

      const sequenceIndex = sequenceSlides.indexOf(closest);
      if (sequenceIndex < 0) return;

      let targetIndex = sequenceIndex + direction;

      if (targetIndex < 0 || targetIndex >= sequenceSlides.length) {
        jumpToRealSlide(activeIndex);

        window.requestAnimationFrame(() => {
          if (!destroyed) navigate(direction);
        });
        return;
      }

      const target = sequenceSlides[targetIndex];

      target.scrollIntoView({
        behavior: hzLifestyleCarouselHpReducedMotionQuery.matches ? "auto" : "smooth",
        block: "nearest",
        inline: "center"
      });

      schedulePositionSync();
    }

    function handleScroll() {
      if (isJumping) return;

      schedulePositionSync();
      clearSettleTimer();

      settleTimerId = window.setTimeout(() => {
        settleTimerId = null;
        normalizeLoopPosition();
      }, 140);
    }

    function handleTrackKeydown(event) {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

      const isRtl = window.getComputedStyle(root).direction === "rtl";
      let direction = event.key === "ArrowRight" ? 1 : -1;

      if (isRtl) {
        direction *= -1;
      }

      event.preventDefault();
      navigate(direction);
    }

    function handleResize() {
      if (destroyed) return;

      clearResizeTimer();

      resizeTimerId = window.setTimeout(() => {
        resizeTimerId = null;
        jumpToRealSlide(activeIndex);
      }, 90);
    }

    function handleReducedMotionChange() {
      schedulePositionSync();
    }

    function selectBlock(blockId) {
      const index = realSlides.findIndex(
        (slide) => slide.dataset.hzLifestyleBlockId === blockId
      );

      if (index < 0) return;

      activeIndex = index;
      jumpToRealSlide(index);
    }

    function destroy() {
      destroyed = true;
      clearSettleTimer();
      clearResizeTimer();

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
        frameId = null;
      }

      track.removeEventListener("scroll", handleScroll);
      track.removeEventListener("keydown", handleTrackKeydown);
      window.removeEventListener("resize", handleResize);
      hzLifestyleCarouselHpReducedMotionQuery.removeEventListener(
        "change",
        handleReducedMotionChange
      );

      previousButton?.removeEventListener("click", handlePrevious);
      nextButton?.removeEventListener("click", handleNext);

      beforeClones.forEach((clone) => clone.remove());
      afterClones.forEach((clone) => clone.remove());
      beforeClones = [];
      afterClones = [];

      realSlides.forEach((slide) => {
        slide.style.removeProperty("--hz-lifestyle-carousel-surface-width");
        slide.style.removeProperty("--hz-lifestyle-carousel-surface-height");
        delete slide.dataset.hzLifestyleLogicalIndex;
      });

      root.classList.remove("hz__lifestyleCarouselHp--normalizing");
      root.style.removeProperty("--hz-lifestyle-carousel-stage-height");
      hzLifestyleCarouselHpControllers.delete(root);
    }

    function handlePrevious() {
      navigate(-1);
    }

    function handleNext() {
      navigate(1);
    }

    track.addEventListener("scroll", handleScroll, { passive: true });
    track.addEventListener("keydown", handleTrackKeydown);
    window.addEventListener("resize", handleResize, { passive: true });
    hzLifestyleCarouselHpReducedMotionQuery.addEventListener(
      "change",
      handleReducedMotionChange
    );

    previousButton?.addEventListener("click", handlePrevious);
    nextButton?.addEventListener("click", handleNext);

    if (realSlides.length <= 1) {
      if (previousButton) previousButton.disabled = true;
      if (nextButton) nextButton.disabled = true;
    }

    hzLifestyleCarouselHpControllers.set(root, { destroy, selectBlock });

    window.requestAnimationFrame(() => {
      jumpToRealSlide(0);
    });
  }

  function hzLifestyleCarouselHpInitWithin(scope = document) {
    if (scope.matches?.("[data-hz-lifestyle-carousel]")) {
      hzLifestyleCarouselHpCreate(scope);
    }

    scope
      .querySelectorAll?.("[data-hz-lifestyle-carousel]")
      .forEach(hzLifestyleCarouselHpCreate);
  }

  function hzLifestyleCarouselHpDestroyWithin(scope) {
    const roots = [];

    if (scope.matches?.("[data-hz-lifestyle-carousel]")) {
      roots.push(scope);
    }

    scope
      .querySelectorAll?.("[data-hz-lifestyle-carousel]")
      .forEach((root) => roots.push(root));

    roots.forEach((root) => {
      hzLifestyleCarouselHpControllers.get(root)?.destroy();
    });
  }

  function hzLifestyleCarouselHpHandleBlockSelect(event) {
    const slide = event.target.closest?.("[data-hz-lifestyle-slide]");
    const root = slide?.closest?.("[data-hz-lifestyle-carousel]");

    if (!slide || !root || slide.hasAttribute("data-hz-lifestyle-clone")) return;

    hzLifestyleCarouselHpControllers
      .get(root)
      ?.selectBlock(slide.dataset.hzLifestyleBlockId);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => hzLifestyleCarouselHpInitWithin(document),
      { once: true }
    );
  } else {
    hzLifestyleCarouselHpInitWithin(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    hzLifestyleCarouselHpInitWithin(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    hzLifestyleCarouselHpDestroyWithin(event.target);
  });

  document.addEventListener(
    "shopify:block:select",
    hzLifestyleCarouselHpHandleBlockSelect
  );
})();

/* --------------------------------------------------------------------------
   AR Social Video HP
   Continuous native-scroll carousel + professional video interactions.
   -------------------------------------------------------------------------- */

(() => {
  "use strict";

  const hzSocialVideoHpControllers = new WeakMap();
  const hzSocialVideoHpMobileQuery = window.matchMedia("(max-width: 767px)");
  const hzSocialVideoHpReducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

  function hzSocialVideoHpCreate(root) {
    if (!root || hzSocialVideoHpControllers.has(root)) return;

    const track = root.querySelector("[data-hz-social-video-track]");
    const realSlides = Array.from(root.querySelectorAll("[data-hz-social-video-slide]"));
    const previousButton = root.querySelector("[data-hz-social-video-previous]");
    const nextButton = root.querySelector("[data-hz-social-video-next]");
    const interaction = root.dataset.hzSocialVideoInteraction || "reveal_pause";

    if (!track || !realSlides.length) return;

    const cloneBufferSize = realSlides.length > 1 ? Math.min(5, realSlides.length) : 0;
    let beforeClones = [];
    let afterClones = [];
    let sequenceSlides = realSlides.slice();
    let activeIndex = 0;
    let frameId = null;
    let settleTimerId = null;
    let resizeTimerId = null;
    let isJumping = false;
    let destroyed = false;
    let initialStateReleased = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let suppressClickUntil = 0;

    const videoStates = realSlides.map((slide) => ({
      slide,
      video: slide.querySelector("[data-hz-social-video]"),
      control: slide.querySelector("[data-hz-social-video-control]"),
      sound: slide.querySelector("[data-hz-social-video-sound]"),
      progressIndicator: slide.querySelector("[data-hz-social-video-progress]"),
      hideTimerId: null,
      progressFrameId: null
    }));

    realSlides.forEach((slide, index) => {
      slide.dataset.hzSocialLogicalIndex = String(index);
    });

    function clearTimer(state) {
      if (state?.hideTimerId !== null) {
        window.clearTimeout(state.hideTimerId);
        state.hideTimerId = null;
      }
    }

    function stopProgress(state) {
      if (state?.progressFrameId !== null) {
        window.cancelAnimationFrame(state.progressFrameId);
        state.progressFrameId = null;
      }
    }

    function updateProgress(state) {
      if (!state?.video || !state.progressIndicator) return;

      const duration = Number.isFinite(state.video.duration) ? state.video.duration : 0;
      const ratio = duration > 0
        ? Math.max(0, Math.min(1, state.video.currentTime / duration))
        : 0;

      state.progressIndicator.style.transform = `scaleX(${ratio.toFixed(6)})`;

      if (!state.video.paused && !state.video.ended) {
        state.progressFrameId = window.requestAnimationFrame(() => updateProgress(state));
      } else {
        state.progressFrameId = null;
      }
    }

    function setControlState(state, nextState, visible = true) {
      if (!state?.control) return;

      state.control.dataset.hzState = nextState;
      state.control.classList.toggle("hz__socialVideoHpControl--visible", visible);
      state.control.setAttribute(
        "aria-label",
        nextState === "play" ? "Play video" : "Pause video"
      );
    }

    function schedulePauseControlHide(state, delay = 1100) {
      if (interaction !== "reveal_pause" || !state?.video || state.video.paused) return;

      clearTimer(state);

      state.hideTimerId = window.setTimeout(() => {
        state.hideTimerId = null;

        if (!state.video.paused) {
          state.control?.classList.remove("hz__socialVideoHpControl--visible");
        }
      }, delay);
    }

    function syncSoundUi(state) {
      if (!state?.sound || !state.video) return;

      const muted = state.video.muted;
      state.sound.setAttribute("aria-pressed", muted ? "true" : "false");
      state.sound.setAttribute("aria-label", muted ? "Unmute video" : "Mute video");
    }

    function resetStateToPlay(state, resetTime = false) {
      if (!state?.video) return;

      clearTimer(state);
      stopProgress(state);

      if (resetTime) {
        try {
          state.video.currentTime = 0;
        } catch (_) {}
      }

      setControlState(state, "play", true);
      updateProgress(state);
      syncSoundUi(state);
    }

    function pauseState(state, { reset = false } = {}) {
      if (!state?.video) return;

      state.video.pause();
      resetStateToPlay(state, reset);
    }

    function getStateByVideo(video) {
      return videoStates.find((state) => state.video === video) || null;
    }

    function pauseVideoElement(video) {
      const state = getStateByVideo(video);

      if (state) {
        pauseState(state);
      } else {
        video.pause();
      }
    }

    function pauseOtherSocialVideos(currentState) {
      document
        .querySelectorAll("[data-hz-social-video-section] [data-hz-social-video]")
        .forEach((video) => {
          if (video === currentState.video) return;

          const ownerRoot = video.closest("[data-hz-social-video-section]");
          const ownerController = ownerRoot
            ? hzSocialVideoHpControllers.get(ownerRoot)
            : null;

          if (ownerController?.pauseVideoElement) {
            ownerController.pauseVideoElement(video);
          } else {
            video.pause();
          }
        });
    }

    async function playState(state) {
      if (!state?.video) return;

      pauseOtherSocialVideos(state);
      clearTimer(state);
      stopProgress(state);

      state.video.loop = false;
      state.video.muted = false;
      state.video.playsInline = true;

      try {
        await state.video.play();
      } catch (_) {
        setControlState(state, "play", true);
        return;
      }

      syncSoundUi(state);
      setControlState(state, "pause", true);
      updateProgress(state);

      if (interaction === "reveal_pause") {
        schedulePauseControlHide(state);
      } else {
        window.setTimeout(() => {
          if (!state.video.paused) {
            state.control?.classList.remove("hz__socialVideoHpControl--visible");
          }
        }, 450);
      }
    }

    function handleControlClick(event, state) {
      event.preventDefault();
      event.stopPropagation();

      if (!state?.video) return;

      if (state.video.paused || state.video.ended) {
        playState(state);
      } else {
        pauseState(state);
      }
    }

    function handleMediaClick(event, state) {
      if (!state?.video || performance.now() < suppressClickUntil) return;

      if (
        event.target.closest?.(
          "[data-hz-social-video-control], [data-hz-social-video-sound], .hz__socialVideoHpContentLink"
        )
      ) {
        return;
      }

      if (state.video.paused) return;

      if (interaction === "tap_pause_sound") {
        pauseState(state);
        return;
      }

      setControlState(state, "pause", true);
      schedulePauseControlHide(state, 1700);
    }

    function handleSoundClick(event, state) {
      event.preventDefault();
      event.stopPropagation();

      if (!state?.video) return;

      state.video.muted = !state.video.muted;
      syncSoundUi(state);
    }

    function handleEnded(state) {
      if (!state?.video) return;
      pauseState(state, { reset: true });
    }

    function handlePlay(state) {
      setControlState(state, "pause", interaction === "reveal_pause");
      stopProgress(state);
      updateProgress(state);
    }

    function handlePause(state) {
      if (state.video?.ended) return;
      resetStateToPlay(state, false);
    }

    videoStates.forEach((state) => {
      if (!state.video) return;

      state.video.loop = false;
      state.video.muted = false;
      state.video.playsInline = true;

      state.control?.addEventListener("click", (event) => handleControlClick(event, state));
      state.sound?.addEventListener("click", (event) => handleSoundClick(event, state));
      state.slide.addEventListener("click", (event) => handleMediaClick(event, state));
      state.video.addEventListener("ended", () => handleEnded(state));
      state.video.addEventListener("play", () => handlePlay(state));
      state.video.addEventListener("pause", () => handlePause(state));
      state.video.addEventListener("loadedmetadata", () => updateProgress(state));

      setControlState(state, "play", true);
      syncSoundUi(state);
      updateProgress(state);
    });

    function sanitizeClone(source, logicalIndex) {
      const clone = source.cloneNode(true);

      clone.classList.add("hz__socialVideoHpSlide--clone");
      clone.setAttribute("data-hz-social-video-clone", "");
      clone.setAttribute("data-hz-social-logical-index", String(logicalIndex));
      clone.setAttribute("aria-hidden", "true");
      clone.setAttribute("inert", "");
      clone.setAttribute("role", "presentation");
      clone.removeAttribute("data-hz-social-video-block-id");

      [clone, ...clone.querySelectorAll("*")].forEach((element) => {
        Array.from(element.attributes).forEach((attribute) => {
          if (attribute.name === "id" || attribute.name.startsWith("data-shopify")) {
            element.removeAttribute(attribute.name);
          }
        });
      });

      clone.querySelectorAll("video").forEach((video) => {
        video.pause();
        video.removeAttribute("data-hz-social-video");
        video.preload = "none";
        video.querySelectorAll("source").forEach((source) => source.remove());
      });

      clone
        .querySelectorAll(
          "[data-hz-social-video-control], [data-hz-social-video-sound], [data-hz-social-video-progress]"
        )
        .forEach((element) => {
          element.removeAttribute("data-hz-social-video-control");
          element.removeAttribute("data-hz-social-video-sound");
          element.removeAttribute("data-hz-social-video-progress");
        });

      return clone;
    }

    if (cloneBufferSize > 0) {
      const beforeFragment = document.createDocumentFragment();
      const afterFragment = document.createDocumentFragment();

      for (let offset = cloneBufferSize; offset >= 1; offset -= 1) {
        const logicalIndex =
          (realSlides.length - (offset % realSlides.length)) % realSlides.length;

        const clone = sanitizeClone(realSlides[logicalIndex], logicalIndex);
        beforeClones.push(clone);
        beforeFragment.appendChild(clone);
      }

      for (let offset = 0; offset < cloneBufferSize; offset += 1) {
        const logicalIndex = offset % realSlides.length;
        const clone = sanitizeClone(realSlides[logicalIndex], logicalIndex);

        afterClones.push(clone);
        afterFragment.appendChild(clone);
      }

      track.insertBefore(beforeFragment, realSlides[0]);
      track.appendChild(afterFragment);
      sequenceSlides = Array.from(track.querySelectorAll("[data-hz-social-video-slide]"));
    }

    function clearSettleTimer() {
      if (settleTimerId !== null) {
        window.clearTimeout(settleTimerId);
        settleTimerId = null;
      }
    }

    function clearResizeTimer() {
      if (resizeTimerId !== null) {
        window.clearTimeout(resizeTimerId);
        resizeTimerId = null;
      }
    }

    function getLogicalIndex(slide) {
      const value = Number(slide?.dataset.hzSocialLogicalIndex);
      return Number.isInteger(value) ? value : -1;
    }

    function getAlignment() {
      if (hzSocialVideoHpMobileQuery.matches) {
        const rect = track.getBoundingClientRect();

        return {
          mode: "center",
          point: rect.left + rect.width / 2,
          inline: "center"
        };
      }

      const rect = track.getBoundingClientRect();
      const paddingLeft =
        Number.parseFloat(window.getComputedStyle(track).paddingLeft) || 0;

      return {
        mode: "start",
        point: rect.left + paddingLeft,
        inline: "start"
      };
    }

    function getClosestSequenceSlide() {
      const alignment = getAlignment();
      let closest = null;
      let closestDistance = Number.POSITIVE_INFINITY;

      sequenceSlides.forEach((slide) => {
        const rect = slide.getBoundingClientRect();
        if (rect.width <= 0) return;

        const slidePoint =
          alignment.mode === "center"
            ? rect.left + rect.width / 2
            : rect.left;

        const distance = Math.abs(alignment.point - slidePoint);

        if (distance < closestDistance) {
          closestDistance = distance;
          closest = slide;
        }
      });

      return closest;
    }

    function setActive(nextIndex) {
      if (!realSlides[nextIndex]) return;

      realSlides.forEach((slide, index) => {
        slide.classList.toggle(
          "hz__socialVideoHpSlide--active",
          index === nextIndex
        );
      });

      activeIndex = nextIndex;
    }

    function updateMobileVisualHeights() {
      if (!hzSocialVideoHpMobileQuery.matches) {
        sequenceSlides.forEach((slide) => {
          slide.style.removeProperty("--hz-social-video-card-height");
        });

        return;
      }

      if (!initialStateReleased) {
        realSlides.forEach((slide) => {
          slide.classList.remove("hz__socialVideoHpSlide--initial");
        });
        initialStateReleased = true;
      }

      const trackRect = track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;
      const gap =
        Number.parseFloat(window.getComputedStyle(track).columnGap) || 10;
      const closestSlide = hzSocialVideoHpReducedMotionQuery.matches
        ? getClosestSequenceSlide()
        : null;

      sequenceSlides.forEach((slide) => {
        const slideRect = slide.getBoundingClientRect();
        if (slideRect.width <= 0) return;

        const activeHeight = slideRect.width * (600 / 447);
        const inactiveHeight = activeHeight * (308 / 344);
        let height = inactiveHeight;

        if (hzSocialVideoHpReducedMotionQuery.matches) {
          if (slide === closestSlide) {
            height = activeHeight;
          }
        } else {
          const slideCenter = slideRect.left + slideRect.width / 2;
          const distance = Math.abs(trackCenter - slideCenter);
          const activationDistance = Math.max(slideRect.width + gap, 1);
          const progress = Math.max(
            0,
            Math.min(1, 1 - distance / activationDistance)
          );

          height =
            inactiveHeight +
            (activeHeight - inactiveHeight) * progress;
        }

        slide.style.setProperty(
          "--hz-social-video-card-height",
          `${height.toFixed(2)}px`
        );
      });
    }

    function updateActiveFromPosition() {
      frameId = null;
      if (destroyed) return;

      updateMobileVisualHeights();

      const closest = getClosestSequenceSlide();
      const logicalIndex = getLogicalIndex(closest);

      if (logicalIndex >= 0) {
        setActive(logicalIndex);
      }
    }

    function schedulePositionSync() {
      if (frameId !== null || destroyed) return;
      frameId = window.requestAnimationFrame(updateActiveFromPosition);
    }

    function jumpToRealSlide(logicalIndex, visualSource = null) {
      const target = realSlides[logicalIndex];
      if (!target || destroyed) return;

      isJumping = true;
      clearSettleTimer();

      const previousSnap = track.style.scrollSnapType;
      const previousBehavior = track.style.scrollBehavior;
      const alignment = getAlignment();
      const targetMedia = target.querySelector(".hz__socialVideoHpMedia");
      const sourceMedia = visualSource?.querySelector(".hz__socialVideoHpMedia");
      let mirroredHeight = null;

      if (
        hzSocialVideoHpMobileQuery.matches &&
        targetMedia &&
        sourceMedia
      ) {
        const sourceHeight = sourceMedia.getBoundingClientRect().height;

        if (Number.isFinite(sourceHeight) && sourceHeight > 0) {
          mirroredHeight = `${sourceHeight.toFixed(2)}px`;
          target.classList.add("hz__socialVideoHpSlide--normalizing");
          target.style.setProperty(
            "--hz-social-video-card-height",
            mirroredHeight
          );
          targetMedia.getBoundingClientRect();
        }
      }

      track.style.scrollSnapType = "none";
      track.style.scrollBehavior = "auto";

      target.scrollIntoView({
        behavior: "auto",
        block: "nearest",
        inline: alignment.inline
      });

      /*
       * Same loop-normalization principle as Collections:
       * recalculate all centered heights synchronously after the hidden
       * teleport, before the browser gets a chance to paint stale geometry.
       */
      updateMobileVisualHeights();
      targetMedia?.getBoundingClientRect();

      window.requestAnimationFrame(() => {
        if (destroyed) return;

        if (mirroredHeight !== null) {
          target.classList.remove("hz__socialVideoHpSlide--normalizing");
        }

        track.style.scrollSnapType = previousSnap;
        track.style.scrollBehavior = previousBehavior;
        isJumping = false;
        schedulePositionSync();
      });
    }

    function normalizeLoopPosition() {
      if (destroyed || isJumping || cloneBufferSize === 0) return;

      const closest = getClosestSequenceSlide();
      if (!closest?.hasAttribute("data-hz-social-video-clone")) return;

      const logicalIndex = getLogicalIndex(closest);
      if (logicalIndex < 0) return;

      jumpToRealSlide(logicalIndex, closest);
    }

    function navigate(direction) {
      if (destroyed || realSlides.length <= 1 || isJumping) return;

      const closest = getClosestSequenceSlide();
      if (!closest) return;

      const sequenceIndex = sequenceSlides.indexOf(closest);
      if (sequenceIndex < 0) return;

      const targetIndex = sequenceIndex + direction;

      if (targetIndex < 0 || targetIndex >= sequenceSlides.length) {
        jumpToRealSlide(activeIndex);

        window.requestAnimationFrame(() => {
          if (!destroyed) navigate(direction);
        });

        return;
      }

      const target = sequenceSlides[targetIndex];
      const alignment = getAlignment();

      target.scrollIntoView({
        behavior: hzSocialVideoHpReducedMotionQuery.matches ? "auto" : "smooth",
        block: "nearest",
        inline: alignment.inline
      });

      schedulePositionSync();
    }

    function handleScroll() {
      if (isJumping) return;

      schedulePositionSync();
      clearSettleTimer();

      settleTimerId = window.setTimeout(() => {
        settleTimerId = null;
        normalizeLoopPosition();
      }, 140);
    }

    function handleTrackKeydown(event) {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

      const isRtl = window.getComputedStyle(root).direction === "rtl";
      let direction = event.key === "ArrowRight" ? 1 : -1;

      if (isRtl) direction *= -1;

      event.preventDefault();
      navigate(direction);
    }

    function handlePointerDown(event) {
      dragStartX = event.clientX;
      dragStartY = event.clientY;
    }

    function handlePointerUp(event) {
      const dx = event.clientX - dragStartX;
      const dy = event.clientY - dragStartY;

      if (Math.hypot(dx, dy) > 8) {
        suppressClickUntil = performance.now() + 280;
      }
    }

    function handleResize() {
      if (destroyed) return;

      clearResizeTimer();

      resizeTimerId = window.setTimeout(() => {
        resizeTimerId = null;
        updateMobileVisualHeights();
        jumpToRealSlide(activeIndex);
      }, 100);
    }

    function selectBlock(blockId) {
      const index = realSlides.findIndex(
        (slide) => slide.dataset.hzSocialVideoBlockId === blockId
      );

      if (index < 0) return;

      activeIndex = index;
      jumpToRealSlide(index);
    }

    function destroy() {
      destroyed = true;
      clearSettleTimer();
      clearResizeTimer();

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
        frameId = null;
      }

      videoStates.forEach((state) => {
        clearTimer(state);
        stopProgress(state);
        state.video?.pause();
      });

      track.removeEventListener("scroll", handleScroll);
      track.removeEventListener("keydown", handleTrackKeydown);
      track.removeEventListener("pointerdown", handlePointerDown);
      track.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("resize", handleResize);

      previousButton?.removeEventListener("click", handlePrevious);
      nextButton?.removeEventListener("click", handleNext);

      beforeClones.forEach((clone) => clone.remove());
      afterClones.forEach((clone) => clone.remove());

      realSlides.forEach((slide) => {
        slide.classList.remove(
          "hz__socialVideoHpSlide--active",
          "hz__socialVideoHpSlide--initial",
          "hz__socialVideoHpSlide--normalizing"
        );
        slide.style.removeProperty("--hz-social-video-card-height");
        delete slide.dataset.hzSocialLogicalIndex;
      });

      hzSocialVideoHpControllers.delete(root);
    }

    function handlePrevious() {
      navigate(-1);
    }

    function handleNext() {
      navigate(1);
    }

    track.addEventListener("scroll", handleScroll, { passive: true });
    track.addEventListener("keydown", handleTrackKeydown);
    track.addEventListener("pointerdown", handlePointerDown, { passive: true });
    track.addEventListener("pointerup", handlePointerUp, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });

    previousButton?.addEventListener("click", handlePrevious);
    nextButton?.addEventListener("click", handleNext);

    hzSocialVideoHpControllers.set(root, {
      destroy,
      selectBlock,
      pauseVideoElement
    });

    window.requestAnimationFrame(() => {
      jumpToRealSlide(0);
    });
  }

  function hzSocialVideoHpInitWithin(scope = document) {
    if (scope.matches?.("[data-hz-social-video-section]")) {
      hzSocialVideoHpCreate(scope);
    }

    scope
      .querySelectorAll?.("[data-hz-social-video-section]")
      .forEach(hzSocialVideoHpCreate);
  }

  function hzSocialVideoHpDestroyWithin(scope) {
    const roots = [];

    if (scope.matches?.("[data-hz-social-video-section]")) {
      roots.push(scope);
    }

    scope
      .querySelectorAll?.("[data-hz-social-video-section]")
      .forEach((root) => roots.push(root));

    roots.forEach((root) => {
      hzSocialVideoHpControllers.get(root)?.destroy();
    });
  }

  function hzSocialVideoHpHandleBlockSelect(event) {
    const slide = event.target.closest?.("[data-hz-social-video-slide]");
    const root = slide?.closest?.("[data-hz-social-video-section]");

    if (!slide || !root || slide.hasAttribute("data-hz-social-video-clone")) return;

    hzSocialVideoHpControllers
      .get(root)
      ?.selectBlock(slide.dataset.hzSocialVideoBlockId);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => hzSocialVideoHpInitWithin(document),
      { once: true }
    );
  } else {
    hzSocialVideoHpInitWithin(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    hzSocialVideoHpInitWithin(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    hzSocialVideoHpDestroyWithin(event.target);
  });

  document.addEventListener(
    "shopify:block:select",
    hzSocialVideoHpHandleBlockSelect
  );
})();

/* --------------------------------------------------------------------------
   AR Newsletter HP
   Native Shopify submission remains authoritative; this controller only adds
   immediate loading feedback and double-submit protection.
   -------------------------------------------------------------------------- */

(() => {
  "use strict";

  const hzNewsletterHpControllers = new WeakMap();

  function hzNewsletterHpCreate(form) {
    if (!form || hzNewsletterHpControllers.has(form)) return;

    const submit = form.querySelector("[data-hz-newsletter-submit]");
    const section = form.closest("[data-hz-newsletter-section]");
    const email = form.querySelector("[data-hz-newsletter-placeholder]");
    const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const shouldAnimatePlaceholder =
      section?.dataset.hzNewsletterTypewriter === "true";
    const fullPlaceholder = email?.dataset.hzNewsletterPlaceholder || "";
    if (!submit) return;

    let placeholderObserver = null;
    let placeholderTimerId = null;
    let placeholderHasRun = false;
    let placeholderIndex = 0;

    function clearPlaceholderTimer() {
      if (placeholderTimerId !== null) {
        window.clearTimeout(placeholderTimerId);
        placeholderTimerId = null;
      }
    }

    function restoreFullPlaceholder() {
      clearPlaceholderTimer();

      if (email) {
        email.placeholder = fullPlaceholder;
      }
    }

    function typeNextCharacter() {
      if (
        !email ||
        email.value ||
        document.activeElement === email ||
        reducedMotionQuery.matches
      ) {
        restoreFullPlaceholder();
        return;
      }

      placeholderIndex += 1;
      email.placeholder = fullPlaceholder.slice(0, placeholderIndex);

      if (placeholderIndex < fullPlaceholder.length) {
        const currentCharacter = fullPlaceholder.charAt(placeholderIndex - 1);
        const delay =
          currentCharacter === " "
            ? 46
            : /[.,!?]/.test(currentCharacter)
              ? 105
              : 58;

        placeholderTimerId = window.setTimeout(typeNextCharacter, delay);
      } else {
        placeholderTimerId = null;
      }
    }

    function startPlaceholderAnimation() {
      if (
        placeholderHasRun ||
        !shouldAnimatePlaceholder ||
        !email ||
        !fullPlaceholder
      ) {
        return;
      }

      placeholderHasRun = true;

      if (
        reducedMotionQuery.matches ||
        email.value ||
        document.activeElement === email
      ) {
        restoreFullPlaceholder();
        return;
      }

      placeholderIndex = 0;
      email.placeholder = "";
      placeholderTimerId = window.setTimeout(typeNextCharacter, 180);
    }

    function setupPlaceholderObserver() {
      if (!email) return;

      if (!shouldAnimatePlaceholder || !("IntersectionObserver" in window)) {
        restoreFullPlaceholder();
        return;
      }

      placeholderObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];

          if (
            entry?.isIntersecting &&
            entry.intersectionRatio >= 0.35
          ) {
            placeholderObserver?.disconnect();
            placeholderObserver = null;
            startPlaceholderAnimation();
          }
        },
        {
          threshold: [0.35]
        }
      );

      placeholderObserver.observe(section || form);
    }

    function handleEmailFocus() {
      restoreFullPlaceholder();
    }

    function handleEmailInput() {
      restoreFullPlaceholder();
    }

    function handleReducedMotionChange() {
      if (reducedMotionQuery.matches) {
        placeholderHasRun = true;
        restoreFullPlaceholder();
        placeholderObserver?.disconnect();
        placeholderObserver = null;
      }
    }

    function setLoading(isLoading) {
      submit.disabled = isLoading;

      if (isLoading) {
        submit.setAttribute("aria-busy", "true");
        form.setAttribute("aria-busy", "true");
      } else {
        submit.removeAttribute("aria-busy");
        form.removeAttribute("aria-busy");
      }
    }

    function handleSubmit() {
      /*
       * The browser fires `submit` only after native constraint validation has
       * passed for a normal user submission. Do not preventDefault here:
       * Shopify/Prestige/CAPTCHA keep full ownership of the actual request.
       */
      setLoading(true);
    }

    function reset() {
      setLoading(false);
    }

    form.addEventListener("submit", handleSubmit);
    email?.addEventListener("focus", handleEmailFocus);
    email?.addEventListener("input", handleEmailInput);
    reducedMotionQuery.addEventListener("change", handleReducedMotionChange);

    setupPlaceholderObserver();

    hzNewsletterHpControllers.set(form, {
      destroy() {
        form.removeEventListener("submit", handleSubmit);
        email?.removeEventListener("focus", handleEmailFocus);
        email?.removeEventListener("input", handleEmailInput);
        reducedMotionQuery.removeEventListener(
          "change",
          handleReducedMotionChange
        );
        placeholderObserver?.disconnect();
        placeholderObserver = null;
        clearPlaceholderTimer();
        restoreFullPlaceholder();
        reset();
        hzNewsletterHpControllers.delete(form);
      },
      reset
    });
  }

  function hzNewsletterHpInitWithin(scope = document) {
    if (scope.matches?.("[data-hz-newsletter-form]")) {
      hzNewsletterHpCreate(scope);
    }

    scope
      .querySelectorAll?.("[data-hz-newsletter-form]")
      .forEach(hzNewsletterHpCreate);
  }

  function hzNewsletterHpDestroyWithin(scope) {
    const forms = [];

    if (scope.matches?.("[data-hz-newsletter-form]")) {
      forms.push(scope);
    }

    scope
      .querySelectorAll?.("[data-hz-newsletter-form]")
      .forEach((form) => forms.push(form));

    forms.forEach((form) => {
      hzNewsletterHpControllers.get(form)?.destroy();
    });
  }

  function hzNewsletterHpResetAll() {
    document
      .querySelectorAll("[data-hz-newsletter-form]")
      .forEach((form) => {
        hzNewsletterHpControllers.get(form)?.reset();
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => hzNewsletterHpInitWithin(document),
      { once: true }
    );
  } else {
    hzNewsletterHpInitWithin(document);
  }

  /*
   * Browsers may restore a page from the back/forward cache with the submit
   * button still disabled. Always restore the interactive state on pageshow.
   */
  window.addEventListener("pageshow", hzNewsletterHpResetAll);

  document.addEventListener("shopify:section:load", (event) => {
    hzNewsletterHpInitWithin(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    hzNewsletterHpDestroyWithin(event.target);
  });
})();

/* --------------------------------------------------------------------------
   AR Testimonials HP
   Manual accessible carousel with guarded fade / slide transitions.
   -------------------------------------------------------------------------- */

(() => {
  "use strict";

  const hzTestimonialsHpControllers = new WeakMap();
  const hzTestimonialsHpReducedMotionQuery = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  );

  function hzTestimonialsHpCreate(root) {
    if (!root || hzTestimonialsHpControllers.has(root)) return;

    const viewport = root.querySelector("[data-hz-testimonials-viewport]");
    const slides = Array.from(root.querySelectorAll("[data-hz-testimonials-slide]"));
    const previousButtons = Array.from(
      root.querySelectorAll("[data-hz-testimonials-previous]")
    );
    const nextButtons = Array.from(
      root.querySelectorAll("[data-hz-testimonials-next]")
    );
    const status = root.querySelector("[data-hz-testimonials-status]");

    if (!viewport || !slides.length) return;

    let activeIndex = Math.max(
      0,
      slides.findIndex((slide) =>
        slide.classList.contains("hz__testimonialsHpSlide--active")
      )
    );
    let transitioning = false;
    let pendingNavigation = null;
    let transitionFallbackId = null;
    let transitionFrameId = null;
    let pointerStartX = 0;
    let pointerStartY = 0;
    let destroyed = false;

    function clearTransitionFallback() {
      if (transitionFallbackId !== null) {
        window.clearTimeout(transitionFallbackId);
        transitionFallbackId = null;
      }
    }

    function clearTransitionFrame() {
      if (transitionFrameId !== null) {
        window.cancelAnimationFrame(transitionFrameId);
        transitionFrameId = null;
      }
    }

    function normalizeIndex(index) {
      return (index + slides.length) % slides.length;
    }

    function updateStatus() {
      if (!status) return;
      status.textContent = `Testimonial ${activeIndex + 1} of ${slides.length}`;
    }

    function clearMotionClasses(slide) {
      slide.classList.remove(
        "hz__testimonialsHpSlide--incoming",
        "hz__testimonialsHpSlide--leaving",
        "hz__testimonialsHpSlide--fromNext",
        "hz__testimonialsHpSlide--fromPrevious",
        "hz__testimonialsHpSlide--toNext",
        "hz__testimonialsHpSlide--toPrevious"
      );
    }

    function finalizeTransition(nextIndex) {
      if (destroyed || !slides[nextIndex]) return;

      clearTransitionFallback();
      clearTransitionFrame();

      slides.forEach((slide, index) => {
        const isActive = index === nextIndex;

        clearMotionClasses(slide);
        slide.classList.toggle("hz__testimonialsHpSlide--active", isActive);
        slide.setAttribute("aria-hidden", isActive ? "false" : "true");

        if (isActive) {
          slide.removeAttribute("inert");
        } else {
          slide.setAttribute("inert", "");
        }
      });

      activeIndex = nextIndex;
      transitioning = false;
      updateStatus();

      if (
        pendingNavigation &&
        pendingNavigation.index !== activeIndex
      ) {
        const queued = pendingNavigation;
        pendingNavigation = null;
        changeSlide(queued.index, queued.direction);
      } else {
        pendingNavigation = null;
      }
    }

    function changeSlide(requestedIndex, directionHint = 0) {
      if (destroyed || slides.length < 2) return;

      const nextIndex = normalizeIndex(requestedIndex);

      if (nextIndex === activeIndex) return;

      let direction = directionHint;

      if (direction === 0) {
        direction = nextIndex > activeIndex ? 1 : -1;
      }

      if (transitioning) {
        pendingNavigation = {
          index: nextIndex,
          direction
        };
        return;
      }

      const outgoing = slides[activeIndex];
      const incoming = slides[nextIndex];

      if (hzTestimonialsHpReducedMotionQuery.matches) {
        finalizeTransition(nextIndex);
        return;
      }

      transitioning = true;

      clearMotionClasses(incoming);
      clearMotionClasses(outgoing);

      incoming.classList.add(
        "hz__testimonialsHpSlide--incoming",
        direction > 0
          ? "hz__testimonialsHpSlide--fromNext"
          : "hz__testimonialsHpSlide--fromPrevious"
      );
      incoming.setAttribute("aria-hidden", "false");
      incoming.removeAttribute("inert");

      outgoing.setAttribute("aria-hidden", "true");
      outgoing.setAttribute("inert", "");

      /*
       * Commit the staged incoming state before motion begins. This avoids
       * intermittent cuts caused by the browser batching both class changes
       * into the same paint.
       */
      incoming.getBoundingClientRect();

      transitionFrameId = window.requestAnimationFrame(() => {
        transitionFrameId = null;
        if (destroyed) return;

        outgoing.classList.remove("hz__testimonialsHpSlide--active");
        outgoing.classList.add(
          "hz__testimonialsHpSlide--leaving",
          direction > 0
            ? "hz__testimonialsHpSlide--toPrevious"
            : "hz__testimonialsHpSlide--toNext"
        );

        incoming.classList.remove(
          "hz__testimonialsHpSlide--incoming",
          "hz__testimonialsHpSlide--fromNext",
          "hz__testimonialsHpSlide--fromPrevious"
        );
        incoming.classList.add("hz__testimonialsHpSlide--active");

        const handleTransitionEnd = (event) => {
          if (event.target !== incoming || event.propertyName !== "opacity") return;

          incoming.removeEventListener("transitionend", handleTransitionEnd);
          finalizeTransition(nextIndex);
        };

        incoming.addEventListener("transitionend", handleTransitionEnd);

        /*
         * transitionend can be skipped by browser lifecycle changes or Theme
         * Editor DOM work. Keep the carousel deterministic with a fallback.
         */
        transitionFallbackId = window.setTimeout(() => {
          incoming.removeEventListener("transitionend", handleTransitionEnd);
          finalizeTransition(nextIndex);
        }, 580);
      });
    }

    function handlePrevious() {
      changeSlide(activeIndex - 1, -1);
    }

    function handleNext() {
      changeSlide(activeIndex + 1, 1);
    }

    function handleKeydown(event) {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

      const isRtl = window.getComputedStyle(root).direction === "rtl";
      let direction = event.key === "ArrowRight" ? 1 : -1;

      if (isRtl) direction *= -1;

      event.preventDefault();
      changeSlide(activeIndex + direction, direction);
    }

    function handlePointerDown(event) {
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
    }

    function handlePointerUp(event) {
      const deltaX = event.clientX - pointerStartX;
      const deltaY = event.clientY - pointerStartY;
      const horizontalDistance = Math.abs(deltaX);
      const verticalDistance = Math.abs(deltaY);

      if (
        horizontalDistance < 48 ||
        horizontalDistance <= verticalDistance * 1.2
      ) {
        return;
      }

      const direction = deltaX < 0 ? 1 : -1;
      changeSlide(activeIndex + direction, direction);
    }

    function selectBlock(blockId) {
      const nextIndex = slides.findIndex(
        (slide) => slide.dataset.hzTestimonialsBlockId === blockId
      );

      if (nextIndex < 0) return;

      if (nextIndex === activeIndex) {
        slides[nextIndex].removeAttribute("inert");
        return;
      }

      const direction = nextIndex > activeIndex ? 1 : -1;
      changeSlide(nextIndex, direction);
    }

    function destroy() {
      destroyed = true;
      clearTransitionFallback();
      clearTransitionFrame();

      previousButtons.forEach((button) => {
        button.removeEventListener("click", handlePrevious);
      });

      nextButtons.forEach((button) => {
        button.removeEventListener("click", handleNext);
      });

      viewport.removeEventListener("keydown", handleKeydown);
      viewport.removeEventListener("pointerdown", handlePointerDown);
      viewport.removeEventListener("pointerup", handlePointerUp);

      hzTestimonialsHpControllers.delete(root);
    }

    previousButtons.forEach((button) => {
      button.addEventListener("click", handlePrevious);
    });

    nextButtons.forEach((button) => {
      button.addEventListener("click", handleNext);
    });

    viewport.addEventListener("keydown", handleKeydown);
    viewport.addEventListener("pointerdown", handlePointerDown, { passive: true });
    viewport.addEventListener("pointerup", handlePointerUp, { passive: true });

    updateStatus();

    hzTestimonialsHpControllers.set(root, {
      destroy,
      selectBlock
    });
  }

  function hzTestimonialsHpInitWithin(scope = document) {
    if (scope.matches?.("[data-hz-testimonials-hp]")) {
      hzTestimonialsHpCreate(scope);
    }

    scope
      .querySelectorAll?.("[data-hz-testimonials-hp]")
      .forEach(hzTestimonialsHpCreate);
  }

  function hzTestimonialsHpDestroyWithin(scope) {
    const roots = [];

    if (scope.matches?.("[data-hz-testimonials-hp]")) {
      roots.push(scope);
    }

    scope
      .querySelectorAll?.("[data-hz-testimonials-hp]")
      .forEach((root) => roots.push(root));

    roots.forEach((root) => {
      hzTestimonialsHpControllers.get(root)?.destroy();
    });
  }

  function hzTestimonialsHpHandleBlockSelect(event) {
    const slide = event.target.closest?.("[data-hz-testimonials-slide]");
    const root = slide?.closest?.("[data-hz-testimonials-hp]");

    if (!slide || !root) return;

    hzTestimonialsHpControllers
      .get(root)
      ?.selectBlock(slide.dataset.hzTestimonialsBlockId);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => hzTestimonialsHpInitWithin(document),
      { once: true }
    );
  } else {
    hzTestimonialsHpInitWithin(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    hzTestimonialsHpInitWithin(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    hzTestimonialsHpDestroyWithin(event.target);
  });

  document.addEventListener(
    "shopify:block:select",
    hzTestimonialsHpHandleBlockSelect
  );
})();

/* --------------------------------------------------------------------------
   AR Product Card — shared card / variant / hover / cart workflow
   -------------------------------------------------------------------------- */

(() => {
  "use strict";

  const hzProductCardControllers = new WeakMap();
  const hzProductCardReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const hzProductCardFineHover = window.matchMedia("(hover: hover) and (pointer: fine)");
  const HOVER_ROTATION_MS = 900;
  const IMAGE_CROSSFADE_MS = 300;

  function hzProductCardCreate(root) {
    if (!root || hzProductCardControllers.has(root)) return;

    const cards = Array.from(root.querySelectorAll("[data-hz-product-card]")).filter(
      (card) => card.closest("[data-hz-product-card-scope]") === root
    );
    if (!cards.length) return;

    const modal = root.querySelector("[data-hz-product-modal]");
    const modalClose = root.querySelector("[data-hz-product-modal-close]");
    const modalTitle = root.querySelector("[data-hz-product-modal-title]");
    const modalImage = root.querySelector("[data-hz-product-modal-image]");
    const modalPrimary = root.querySelector("[data-hz-product-modal-primary]");
    const modalOptions = root.querySelector("[data-hz-product-modal-options]");
    const modalCurrentPrice = root.querySelector("[data-hz-product-modal-current-price]");
    const modalComparePrice = root.querySelector("[data-hz-product-modal-compare-price]");
    const modalAdd = root.querySelector("[data-hz-product-modal-add]");
    const modalAddLabel = root.querySelector("[data-hz-product-modal-add-label]");
    const modalStatus = root.querySelector("[data-hz-product-modal-status]");
    const modalStatusText = root.querySelector("[data-hz-product-modal-status-text]");
    const modalProductLink = root.querySelector("[data-hz-product-modal-product-link]");

    let activeModalCard = null;
    let activeModalData = null;
    let activeSelections = [];
    let activeVariant = null;
    let modalCloseTimer = null;
    let hoverPrimeObserver = null;
    let destroyed = false;
    const requestControllers = new Map();
    const hoverPreloadCache = new Set();

    const labelsFor = (card) => ({
      add: card?.dataset.hzProductCardAddLabel || root.dataset.hzProductCardAddLabel || "Add To Cart",
      adding: card?.dataset.hzProductCardAddingLabel || root.dataset.hzProductCardAddingLabel || "Adding...",
      added: card?.dataset.hzProductCardAddedLabel || root.dataset.hzProductCardAddedLabel || "Added To Cart",
      soldOut: card?.dataset.hzProductCardSoldOutLabel || root.dataset.hzProductCardSoldOutLabel || "Sold Out"
    });

    const parseProductData = (card) => {
      if (card.hzProductData) return card.hzProductData;
      const script = card.querySelector("[data-hz-product-data]");
      if (!script) return null;

      try {
        card.hzProductData = JSON.parse(script.textContent || "{}");
        return card.hzProductData;
      } catch (error) {
        console.error("[AR Product Card] Invalid product JSON", error);
        return null;
      }
    };

    const normalizeUrl = (value) => {
      if (!value) return "";
      try {
        const url = new URL(value, window.location.href);
        return `${url.origin}${url.pathname}`;
      } catch (_) {
        return String(value).split("?")[0];
      }
    };

    const getCardImage = (card) => card.querySelector("[data-hz-product-image]");
    const getCardTransitionImage = (card) => card.querySelector("[data-hz-product-image-transition]");
    const getCardImageLink = (card) => card.querySelector(".hz__productCardImageLink");
    const getCardMedia = (card) => card.querySelector(".hz__productCardMedia");

    const rememberBaseImage = (card, src, alt) => {
      if (!src) return;
      card.hzBaseImage = { src, alt: alt || "" };
    };

    const commitPrimaryImage = (card, src, alt) => {
      const image = getCardImage(card);
      if (!image || !src) return false;
      image.src = src;
      image.srcset = "";
      if (alt !== undefined && alt !== null) image.alt = alt;
      return true;
    };

    const settleImageTransition = (card, { commitVisible = false, resolveValue = false } = {}) => {
      const link = getCardImageLink(card);
      const transitionImage = getCardTransitionImage(card);

      if (card.hzImageTransitionTimer) {
        window.clearTimeout(card.hzImageTransitionTimer);
        card.hzImageTransitionTimer = null;
      }

      if (
        commitVisible &&
        link?.dataset.hzImageTransitioning === "true" &&
        card.hzTransitionSrc
      ) {
        commitPrimaryImage(card, card.hzTransitionSrc, card.hzTransitionAlt);
      }

      if (link) link.dataset.hzImageTransitioning = "false";
      if (transitionImage) transitionImage.removeAttribute("data-hz-transition-ready");

      card.hzTransitionSrc = "";
      card.hzTransitionAlt = "";

      if (card.hzImageTransitionResolve) {
        const resolve = card.hzImageTransitionResolve;
        card.hzImageTransitionResolve = null;
        resolve(resolveValue);
      }
    };

    const setImage = (card, src, alt, { asBase = false } = {}) => {
      const image = getCardImage(card);
      const transitionImage = getCardTransitionImage(card);
      const link = getCardImageLink(card);
      if (!image || !src) return Promise.resolve(false);

      // A new request can arrive during a hover rotation or while the pointer is
      // leaving. Commit whichever frame is visibly on top first, then crossfade
      // from that stable state to the newest request.
      settleImageTransition(card, { commitVisible: true, resolveValue: true });

      if (asBase) rememberBaseImage(card, src, alt || image.alt || "");

      const requestToken = (card.hzImageRequestToken || 0) + 1;
      card.hzImageRequestToken = requestToken;

      if (normalizeUrl(image.currentSrc || image.src) === normalizeUrl(src)) {
        if (alt !== undefined && alt !== null) image.alt = alt;
        return Promise.resolve(true);
      }

      if (hzProductCardReducedMotion.matches || !transitionImage || !link) {
        commitPrimaryImage(card, src, alt);
        return Promise.resolve(true);
      }

      return new Promise((resolve) => {
        const preloader = new Image();
        preloader.onload = () => {
          if (destroyed || card.hzImageRequestToken !== requestToken) {
            resolve(false);
            return;
          }

          transitionImage.src = src;
          transitionImage.alt = "";
          card.hzTransitionSrc = src;
          card.hzTransitionAlt = alt || "";
          card.hzImageTransitionResolve = resolve;

          // Reset the overlay before activating it so even consecutive image
          // changes get a real opacity transition rather than a source flash.
          link.dataset.hzImageTransitioning = "false";
          transitionImage.setAttribute("data-hz-transition-ready", "true");
          void transitionImage.offsetWidth;
          link.dataset.hzImageTransitioning = "true";

          card.hzImageTransitionTimer = window.setTimeout(() => {
            if (destroyed || card.hzImageRequestToken !== requestToken) {
              settleImageTransition(card, { commitVisible: false, resolveValue: false });
              return;
            }

            commitPrimaryImage(card, src, alt);
            card.hzImageTransitionTimer = null;
            link.dataset.hzImageTransitioning = "false";
            transitionImage.removeAttribute("data-hz-transition-ready");
            card.hzTransitionSrc = "";
            card.hzTransitionAlt = "";

            if (card.hzImageTransitionResolve) {
              const finish = card.hzImageTransitionResolve;
              card.hzImageTransitionResolve = null;
              finish(true);
            }
          }, IMAGE_CROSSFADE_MS);
        };
        preloader.onerror = () => {
          resolve(false);
        };
        preloader.src = src;
      });
    };

    const setPrice = (card, priceFormatted, compareFormatted) => {
      const price = card.querySelector("[data-hz-product-price]");
      const compare = card.querySelector("[data-hz-product-compare]");

      if (price && priceFormatted) price.textContent = priceFormatted;
      if (compare) {
        compare.textContent = compareFormatted || "";
        compare.classList.toggle("hz__productCardCompare--hidden", !compareFormatted);
      }
    };

    const getVariantInput = (card) => card.querySelector("[data-hz-product-card-variant-input]");
    const getCardButton = (card) => card.querySelector("[data-hz-product-add]");
    const getCardButtonLabel = (card) => card.querySelector("[data-hz-product-add-label]");

    const setCardVariant = (card, variant) => {
      if (!variant) return;
      card.dataset.hzRepresentativeVariant = String(variant.id);
      const input = getVariantInput(card);
      if (input) input.value = String(variant.id);
    };

    const setCardButtonState = (card, variant, { preserveBusy = true } = {}) => {
      const button = getCardButton(card);
      const label = getCardButtonLabel(card);
      if (!button || !variant) return;

      const isBusy = preserveBusy && button.getAttribute("aria-busy") === "true";
      button.disabled = !variant.available || isBusy;
      if (label && !isBusy) {
        const labels = labelsFor(card);
        label.textContent = variant.available ? labels.add : labels.soldOut;
      }
    };

    const clearCardStatus = (card) => {
      const status = card.querySelector("[data-hz-product-card-status]");
      if (!status) return;
      status.textContent = "";
      status.hidden = true;
    };

    const showCardStatus = (card, message) => {
      const status = card.querySelector("[data-hz-product-card-status]");
      if (!status) return;
      status.textContent = message || "Unable to add this item to the cart.";
      status.hidden = false;
    };

    const findRepresentativeVariant = (data, firstValue) => {
      if (!data) return null;
      return (
        data.variants.find((variant) => variant.available && variant.options?.[0] === firstValue) ||
        data.variants.find((variant) => variant.options?.[0] === firstValue) ||
        null
      );
    };

    const getSelectedFirstValue = (card, data) =>
      card.dataset.hzSelectedFirstOption ||
      card.querySelector("[data-hz-product-swatch][aria-pressed='true']")?.dataset.hzOptionValue ||
      data?.variants?.find((variant) => variant.available)?.options?.[0] ||
      data?.variants?.[0]?.options?.[0] ||
      "";

    const getRepresentativeVariant = (card, data) => {
      const firstValue = getSelectedFirstValue(card, data);
      const storedId = String(card.dataset.hzRepresentativeVariant || "");
      const storedVariant = data?.variants?.find((variant) => String(variant.id) === storedId);

      if (storedVariant && (!firstValue || storedVariant.options?.[0] === firstValue)) {
        return storedVariant;
      }

      return (
        findRepresentativeVariant(data, firstValue) ||
        data?.variants?.find((variant) => variant.available) ||
        data?.variants?.[0] ||
        null
      );
    };

    const quantityOneConstraint = (variant) => {
      if (!variant) return "This variant is unavailable.";
      if (variant.requiresSellingPlan) {
        return "This product requires a purchase option. Choose the purchase option on the product page before adding it to your cart.";
      }

      const rule = variant.quantityRule || {};
      const min = Number(rule.min ?? 1);
      const max = rule.max === null || rule.max === undefined || rule.max === "" ? null : Number(rule.max);
      const increment = Math.max(1, Number(rule.increment ?? 1));

      if (Number.isFinite(min) && min > 1) {
        return `This variant requires a minimum quantity of ${min}. Choose your quantity on the product page.`;
      }
      if (max !== null && Number.isFinite(max) && max < 1) {
        return `This variant has a maximum quantity of ${max}. Choose an available quantity on the product page.`;
      }
      if (Number.isFinite(increment) && 1 % increment !== 0) {
        return `This variant must be purchased in quantity increments of ${increment}. Choose your quantity on the product page.`;
      }
      return "";
    };

    const normalizeOptionText = (value) => String(value || "").trim().toLowerCase();

    const parsePrestigeImageGroup = (alt, optionNames = []) => {
      if (!alt || !String(alt).includes("#")) return null;
      const groupText = String(alt).split("#").pop()?.trim();
      if (!groupText) return null;

      // Match known Shopify option names first. This preserves spaces and
      // underscores inside values such as "14k Gold" or "Rose_Gold".
      const orderedOptionNames = [...optionNames]
        .filter(Boolean)
        .sort((a, b) => String(b).length - String(a).length);
      const normalizedGroup = normalizeOptionText(groupText);

      for (const optionName of orderedOptionNames) {
        const prefix = `${String(optionName).trim()}_`;
        if (!normalizedGroup.startsWith(normalizeOptionText(prefix))) continue;
        const optionValue = groupText.slice(prefix.length).trim();
        if (optionValue) return { optionName: String(optionName).trim(), optionValue };
      }

      const separatorIndex = groupText.indexOf("_");
      if (separatorIndex <= 0 || separatorIndex >= groupText.length - 1) return null;
      const optionName = groupText.slice(0, separatorIndex).trim();
      const optionValue = groupText.slice(separatorIndex + 1).trim();
      return optionName && optionValue ? { optionName, optionValue } : null;
    };

    const dedupeImages = (images) => {
      const seen = new Set();
      return (images || []).filter((image) => {
        if (!image?.src) return false;
        const key = normalizeUrl(image.src);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    };

    const getHoverImageScope = (card) =>
      card.dataset.hzProductCardHoverImageScope ||
      root.dataset.hzProductCardHoverImageScope ||
      "variant_group";

    const getVariantGroupedImages = (data, representative, allImages) => {
      const selectedOptions = representative?.options || [];
      const optionNames = data?.optionNames || [];
      if (!selectedOptions.length || !optionNames.length) return [];

      // A single image can only carry one Prestige group suffix. Prefer the
      // earliest product option that has a matching image group (normally the
      // first/card-swatch option), instead of mixing Material + Size groups.
      for (let optionIndex = 0; optionIndex < optionNames.length; optionIndex += 1) {
        const selectedValue = selectedOptions[optionIndex];
        if (!selectedValue) continue;

        const grouped = allImages.filter((image) => {
          const group = parsePrestigeImageGroup(image.alt, optionNames);
          if (!group) return false;
          return (
            normalizeOptionText(group.optionName) === normalizeOptionText(optionNames[optionIndex]) &&
            normalizeOptionText(group.optionValue) === normalizeOptionText(selectedValue)
          );
        });

        if (grouped.length) return grouped;
      }

      return [];
    };

    const getHoverImages = (card, data) => {
      const allImages = dedupeImages(data?.images || []);
      if (getHoverImageScope(card) === "all_product_images") return allImages;

      const representative = getRepresentativeVariant(card, data);
      return getVariantGroupedImages(data, representative, allImages);
    };

    const getSecondHoverImage = (card, pool) => {
      if (pool.length < 2) return null;
      const baseUrl = normalizeUrl(
        card.hzBaseImage?.src || getCardImage(card)?.currentSrc || getCardImage(card)?.src
      );
      const candidate = pool[1] || null;
      return normalizeUrl(candidate?.src) === baseUrl
        ? pool.find((image) => normalizeUrl(image.src) !== baseUrl) || null
        : candidate;
    };

    const getRotationSequence = (card, pool, data) => {
      if (!pool.length) return [];
      const baseSrc = card.hzBaseImage?.src || getCardImage(card)?.currentSrc || getCardImage(card)?.src || "";
      const baseUrl = normalizeUrl(baseSrc);
      const rotationPool = [...pool];

      // "All product images" includes the currently displayed image even when
      // it is not represented in product.images. Variant-only remains strict:
      // only the selected alt group is eligible.
      if (
        getHoverImageScope(card) === "all_product_images" &&
        baseSrc &&
        !rotationPool.some((image) => normalizeUrl(image.src) === baseUrl)
      ) {
        rotationPool.unshift({ src: baseSrc, alt: card.hzBaseImage?.alt || data?.title || "" });
      }

      if (rotationPool.length < 2) return [];
      const baseIndex = rotationPool.findIndex((image) => normalizeUrl(image.src) === baseUrl);
      const startIndex = baseIndex >= 0 ? (baseIndex + 1) % rotationPool.length : 0;
      return Array.from(
        { length: rotationPool.length },
        (_, offset) => rotationPool[(startIndex + offset) % rotationPool.length]
      );
    };

    const preloadHoverImages = (images, limit = images.length) => {
      images.slice(0, Math.max(0, limit)).forEach((image) => {
        if (!image?.src) return;
        const key = normalizeUrl(image.src);
        if (!key || hoverPreloadCache.has(key)) return;
        hoverPreloadCache.add(key);
        const preloader = new Image();
        preloader.src = image.src;
      });
    };

    const primeHoverMedia = (card) => {
      if (!hzProductCardFineHover.matches || destroyed) return;
      const data = parseProductData(card);
      const pool = getHoverImages(card, data);
      if (pool.length < 2) return;
      const behavior =
        card.dataset.hzProductCardHoverBehavior ||
        root.dataset.hzProductCardHoverBehavior ||
        "second_image";
      const firstNext =
        behavior === "rotate"
          ? getRotationSequence(card, pool, data)[0]
          : getSecondHoverImage(card, pool);
      if (firstNext) preloadHoverImages([firstNext], 1);
    };

    const stopHover = (card, { restore = true } = {}) => {
      if (card.hzHoverTimer) {
        window.clearInterval(card.hzHoverTimer);
        card.hzHoverTimer = null;
      }
      card.dataset.hzHoverActive = "false";
      card.hzHoverIndex = 0;

      if (restore && card.hzBaseImage?.src) {
        setImage(card, card.hzBaseImage.src, card.hzBaseImage.alt);
      }
    };

    const startHover = (card) => {
      if (!hzProductCardFineHover.matches || activeModalCard || destroyed) return;
      const behavior =
        card.dataset.hzProductCardHoverBehavior ||
        root.dataset.hzProductCardHoverBehavior ||
        "second_image";
      const data = parseProductData(card);
      const pool = getHoverImages(card, data);
      if (pool.length < 2) return;

      stopHover(card, { restore: false });
      card.dataset.hzHoverActive = "true";
      card.hzHoverIndex = 0;

      if (behavior !== "rotate" || hzProductCardReducedMotion.matches) {
        const secondImage = getSecondHoverImage(card, pool);
        if (secondImage) {
          preloadHoverImages([secondImage], 1);
          setImage(card, secondImage.src, secondImage.alt || data?.title || "").then((success) => {
            if (!success && card.dataset.hzHoverActive === "true") stopHover(card);
          });
        }
        return;
      }

      const sequence = getRotationSequence(card, pool, data);
      if (sequence.length < 2) {
        stopHover(card, { restore: false });
        return;
      }
      preloadHoverImages(sequence);
      const showNext = () => {
        if (destroyed || card.dataset.hzHoverActive !== "true") return;
        const image = sequence[card.hzHoverIndex % sequence.length];
        card.hzHoverIndex += 1;
        setImage(card, image.src, image.alt || data?.title || "");
      };

      showNext();
      card.hzHoverTimer = window.setInterval(showNext, HOVER_ROTATION_MS);
    };

    const selectFirstOption = (swatch) => {
      const card = swatch.closest("[data-hz-product-card]");
      if (!card) return;
      const data = parseProductData(card);
      if (!data) return;

      const media = getCardMedia(card);
      const wasHovered = card.dataset.hzHoverActive === "true" || Boolean(media?.matches(":hover"));
      stopHover(card);
      clearCardStatus(card);

      const firstValue = swatch.dataset.hzOptionValue || "";
      const representative = findRepresentativeVariant(data, firstValue);

      card.querySelectorAll("[data-hz-product-swatch]").forEach((button) => {
        const selected = button === swatch;
        button.classList.toggle("hz__productCardSwatch--active", selected);
        button.setAttribute("aria-pressed", selected ? "true" : "false");
      });
      card.dataset.hzSelectedFirstOption = firstValue;

      if (!representative) return;

      setCardVariant(card, representative);
      setPrice(card, representative.priceFormatted, representative.compareAtFormatted);
      setCardButtonState(card, representative, { preserveBusy: false });

      const fallbackImage = swatch.dataset.hzRepresentativeImage || "";
      const nextImage = representative.image || fallbackImage;
      const nextAlt = representative.imageAlt || swatch.dataset.hzRepresentativeImageAlt || data.title || "";
      if (nextImage) setImage(card, nextImage, nextAlt, { asBase: true });
      primeHoverMedia(card);

      if (wasHovered && hzProductCardFineHover.matches) {
        window.setTimeout(() => {
          if (media?.matches(":hover") && !activeModalCard) startHover(card);
        }, 0);
      }
    };

    const partialVariantExists = (data, selections, optionIndex, candidateValue) =>
      data.variants.some((variant) => {
        if (!variant.available) return false;
        return variant.options.every((value, index) => {
          if (index === optionIndex) return value === candidateValue;
          const selected = selections[index];
          return !selected || value === selected;
        });
      });

    const findExactVariant = (data, selections) =>
      data.variants.find(
        (variant) =>
          variant.options.length === selections.length &&
          variant.options.every((value, index) => value === selections[index])
      ) || null;

    const setModalStatus = (message = "") => {
      if (!modalStatus) return;
      if (modalStatusText) modalStatusText.textContent = message;
      else modalStatus.textContent = message;
      modalStatus.hidden = !message;
    };

    const setModalProductLink = (data, visible) => {
      if (!modalProductLink) return;
      modalProductLink.href = data?.url || "#";
      modalProductLink.hidden = !visible;
    };

    const setModalBusy = (busy) => {
      if (!modalAdd) return;
      if (busy) {
        modalAdd.setAttribute("aria-busy", "true");
        modalAdd.disabled = true;
        if (modalAddLabel) modalAddLabel.textContent = labelsFor(activeModalCard).adding;
      } else {
        modalAdd.removeAttribute("aria-busy");
      }
    };

    const updateModalAvailability = () => {
      if (!activeModalData || !activeModalCard || !modalOptions) return;

      modalOptions.querySelectorAll("[data-hz-modal-choice]").forEach((button) => {
        const index = Number(button.dataset.hzOptionIndex);
        const value = button.dataset.hzOptionValue || "";
        const possible = partialVariantExists(activeModalData, activeSelections, index, value);
        button.disabled = !possible;
        const selected = activeSelections[index] === value;
        button.setAttribute("aria-pressed", selected ? "true" : "false");
        button.classList.toggle("hz__productCardModalChoice--active", selected);
      });

      activeVariant = findExactVariant(activeModalData, activeSelections);
      const variantInput = getVariantInput(activeModalCard);
      const constraint = activeVariant?.available ? quantityOneConstraint(activeVariant) : "";

      if (activeVariant) {
        setCardVariant(activeModalCard, activeVariant);
        setPrice(activeModalCard, activeVariant.priceFormatted, activeVariant.compareAtFormatted);

        if (modalCurrentPrice) modalCurrentPrice.textContent = activeVariant.priceFormatted || "";
        if (modalComparePrice) {
          modalComparePrice.textContent = activeVariant.compareAtFormatted || "";
          modalComparePrice.hidden = !activeVariant.compareAtFormatted;
        }

        if (activeVariant.image) {
          setImage(activeModalCard, activeVariant.image, activeVariant.imageAlt, { asBase: true });
          if (modalImage) {
            modalImage.src = activeVariant.image;
            modalImage.hidden = false;
          }
        }

        if (variantInput) variantInput.value = String(activeVariant.id);

        setModalBusy(false);
        if (modalAdd) modalAdd.disabled = !activeVariant.available || Boolean(constraint);
        if (modalAddLabel) {
          const labels = labelsFor(activeModalCard);
          modalAddLabel.textContent = activeVariant.available ? labels.add : labels.soldOut;
        }
        setModalStatus(constraint);
        setModalProductLink(activeModalData, true);
      } else {
        setModalBusy(false);
        if (modalAdd) modalAdd.disabled = true;
        if (modalAddLabel) modalAddLabel.textContent = labelsFor(activeModalCard).soldOut;
        if (modalCurrentPrice) modalCurrentPrice.textContent = "";
        if (modalComparePrice) {
          modalComparePrice.textContent = "";
          modalComparePrice.hidden = true;
        }
        setModalStatus("This option combination is unavailable.");
        setModalProductLink(activeModalData, true);
      }
    };

    const resetModalState = () => {
      setModalBusy(false);
      activeModalCard = null;
      activeModalData = null;
      activeSelections = [];
      activeVariant = null;
      if (modalOptions) modalOptions.innerHTML = "";
      if (modalPrimary) modalPrimary.textContent = "";
      if (modalImage) {
        modalImage.hidden = true;
        modalImage.removeAttribute("src");
      }
      if (modalCurrentPrice) modalCurrentPrice.textContent = "";
      if (modalComparePrice) {
        modalComparePrice.textContent = "";
        modalComparePrice.hidden = true;
      }
      setModalStatus("");
      setModalProductLink(null, false);
    };

    const closeModal = ({ immediate = false } = {}) => {
      if (modalCloseTimer) {
        window.clearTimeout(modalCloseTimer);
        modalCloseTimer = null;
      }

      if (!modal?.open) {
        resetModalState();
        return;
      }

      modal.classList.remove("hz__productCardModal--visible");
      const finish = () => {
        if (modal.open) modal.close();
        resetModalState();
      };

      if (immediate || hzProductCardReducedMotion.matches) {
        finish();
      } else {
        modalCloseTimer = window.setTimeout(finish, 320);
      }
    };

    const openVariantModal = (card, data, representative, initialMessage = "") => {
      if (!modal || !modalOptions || !data) {
        if (initialMessage) showCardStatus(card, initialMessage);
        return;
      }

      cards.forEach((item) => stopHover(item));
      clearCardStatus(card);
      activeModalCard = card;
      activeModalData = data;

      const selectedFirst = getSelectedFirstValue(card, data);
      const startingVariant =
        representative ||
        findRepresentativeVariant(data, selectedFirst) ||
        data.variants.find((variant) => variant.available) ||
        data.variants[0] ||
        null;

      activeSelections = Array.from(
        { length: data.optionNames.length },
        (_, index) => startingVariant?.options?.[index] || ""
      );
      if (activeSelections.length) activeSelections[0] = selectedFirst || activeSelections[0];

      modalOptions.innerHTML = "";
      data.optionNames.forEach((optionName, optionIndex) => {
        if (optionIndex === 0) return;

        const group = document.createElement("div");
        group.className = "hz__productCardModalGroup";

        const title = document.createElement("h3");
        title.className = "hz__productCardModalGroupTitle";
        title.textContent = optionName;

        const choices = document.createElement("div");
        choices.className = "hz__productCardModalChoices";

        const values = [];
        data.variants.forEach((variant) => {
          const value = variant.options?.[optionIndex];
          if (value && !values.includes(value)) values.push(value);
        });

        values.forEach((value) => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "hz__productCardModalChoice";
          button.dataset.hzModalChoice = "";
          button.dataset.hzOptionIndex = String(optionIndex);
          button.dataset.hzOptionValue = value;
          button.textContent = value;
          choices.appendChild(button);
        });

        group.append(title, choices);
        modalOptions.appendChild(group);
      });

      if (modalTitle) modalTitle.textContent = data.title || "Choose your options";
      if (modalPrimary) {
        const firstOptionName = data.optionNames?.[0] || "Option";
        modalPrimary.textContent = selectedFirst ? `${firstOptionName}: ${selectedFirst}` : "";
      }

      if (modalImage) {
        const cardImage = getCardImage(card);
        const src = startingVariant?.image || card.hzBaseImage?.src || cardImage?.currentSrc || cardImage?.src || "";
        if (src) {
          modalImage.src = src;
          modalImage.alt = "";
          modalImage.hidden = false;
        } else {
          modalImage.hidden = true;
          modalImage.removeAttribute("src");
        }
      }

      if (modalCloseTimer) window.clearTimeout(modalCloseTimer);
      modalCloseTimer = null;
      if (!modal.open) modal.showModal();
      requestAnimationFrame(() => modal.classList.add("hz__productCardModal--visible"));

      updateModalAvailability();
      if (initialMessage) {
        setModalStatus(initialMessage);
        setModalProductLink(data, true);
        if (modalAdd) modalAdd.disabled = true;
      }
    };

    const handleAddButtonClick = (event, button) => {
      const card = button.closest("[data-hz-product-card]");
      if (!card) return;
      const data = parseProductData(card);
      if (!data) return;

      clearCardStatus(card);
      const representative = getRepresentativeVariant(card, data);
      if (!representative) {
        event.preventDefault();
        showCardStatus(card, "This product does not have an available variant to add.");
        return;
      }

      setCardVariant(card, representative);

      if (data.optionNames.length > 1) {
        event.preventDefault();
        openVariantModal(card, data, representative);
        return;
      }

      const constraint = quantityOneConstraint(representative);
      if (constraint) {
        event.preventDefault();
        openVariantModal(card, data, representative, constraint);
      }
    };

    const handleModalAdd = () => {
      if (!activeModalCard || !activeVariant || !activeVariant.available || !modalAdd) return;
      const constraint = quantityOneConstraint(activeVariant);
      if (constraint) {
        setModalStatus(constraint);
        setModalProductLink(activeModalData, true);
        modalAdd.disabled = true;
        return;
      }

      clearCardStatus(activeModalCard);
      setModalStatus("");
      setModalProductLink(activeModalData, true);
      setCardVariant(activeModalCard, activeVariant);

      const form = activeModalCard.querySelector(".hz__productCardForm");
      const submit = getCardButton(activeModalCard);
      if (!form || !submit) {
        setModalStatus("This item cannot be added from this card. Open the product page to continue.");
        setModalProductLink(activeModalData, true);
        return;
      }

      submit.disabled = false;
      setModalBusy(true);
      form.requestSubmit(submit);
    };

    const handleRootClick = (event) => {
      const swatch = event.target.closest("[data-hz-product-swatch]");
      if (swatch && root.contains(swatch)) {
        selectFirstOption(swatch);
        return;
      }

      const add = event.target.closest("[data-hz-product-add]");
      if (add && root.contains(add)) {
        handleAddButtonClick(event, add);
        return;
      }

      const modalChoice = event.target.closest("[data-hz-modal-choice]");
      if (modalChoice && modal?.contains(modalChoice)) {
        const index = Number(modalChoice.dataset.hzOptionIndex);
        activeSelections[index] = modalChoice.dataset.hzOptionValue || "";
        setModalStatus("");
        setModalProductLink(activeModalData, true);
        updateModalAvailability();
        return;
      }

      if (event.target === modal || event.target.closest("[data-hz-product-modal-close]")) {
        closeModal();
      }
    };

    const dispatchCartError = (form, message) => {
      form.dispatchEvent(
        new CustomEvent("cart:error", {
          bubbles: true,
          detail: { error: message || "Unable to add this item to the cart." }
        })
      );
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    };

    const fetchCartState = async (signal) => {
      const routeRoot = window.Shopify?.routes?.root || "/";
      let lastError = null;

      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const response = await fetch(`${routeRoot}cart.js`, {
            headers: { Accept: "application/json" },
            signal
          });
          if (!response.ok) throw new Error(`Cart refresh failed with status ${response.status}.`);
          return await response.json();
        } catch (error) {
          if (error?.name === "AbortError") throw error;
          lastError = error;
          if (attempt === 0) await new Promise((resolve) => window.setTimeout(resolve, 180));
        }
      }

      throw lastError || new Error("Unable to refresh the cart.");
    };

    const submitCardForm = async (form, card) => {
      if (!form || !card || form.hzCartRequestPending) return;
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      const button = getCardButton(card);
      const label = getCardButtonLabel(card);
      const labels = labelsFor(card);
      const controller = new AbortController();
      requestControllers.set(form, controller);
      form.hzCartRequestPending = true;

      clearCardStatus(card);
      if (button) button.setAttribute("aria-busy", "true");
      if (label) label.textContent = labels.adding;
      if (activeModalCard === card) {
        setModalStatus("");
        setModalBusy(true);
      }

      document.documentElement.dispatchEvent(
        new CustomEvent("theme:loading:start", { bubbles: true })
      );

      let addSucceeded = false;

      try {
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:prepare-bundled-sections", {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          })
        );

        const formData = new FormData(form);
        const variantId = String(formData.get("id") || "").trim();
        if (!variantId) {
          dispatchCartError(
            form,
            "This product does not have a valid variant selected. Choose an option and try again."
          );
          return;
        }

        const routeRoot = window.Shopify?.routes?.root || "/";
        formData.set("sections", sectionsToBundle.join(","));
        formData.set("sections_url", `${routeRoot}variants/${variantId}`);

        const response = await fetch(`${routeRoot}cart/add.js`, {
          method: "POST",
          body: formData,
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          signal: controller.signal
        });

        let responseJson = {};
        try {
          responseJson = await response.json();
        } catch (_) {}

        if (!response.ok) {
          dispatchCartError(
            form,
            responseJson?.description ||
              responseJson?.message ||
              `Unable to add this item to the cart (${response.status}).`
          );
          return;
        }

        addSucceeded = true;

        if (
          window.themeVariables?.settings?.cartType === "page" ||
          window.themeVariables?.settings?.pageType === "cart"
        ) {
          window.location.href = `${routeRoot}cart`;
          return;
        }

        let cartContent;
        try {
          cartContent = await fetchCartState(controller.signal);
        } catch (error) {
          if (error?.name === "AbortError") throw error;

          const warning =
            "The item was added, but the cart display could not refresh. Reload the page to see the latest cart.";
          if (button) button.disabled = true;
          if (label) label.textContent = labels.added;
          if (activeModalCard === card && modal?.open) {
            clearCardStatus(card);
            setModalBusy(false);
            if (modalAdd) modalAdd.disabled = true;
            if (modalAddLabel) modalAddLabel.textContent = labels.added;
            setModalStatus(warning);
            setModalProductLink(activeModalData, true);
          } else {
            showCardStatus(card, warning);
          }
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          return;
        }

        cartContent.sections = responseJson?.sections || {};
        const onSuccessDo = formData.get("on_success");

        form.dispatchEvent(
          new CustomEvent("variant:add", {
            bubbles: true,
            detail: {
              items: Object.prototype.hasOwnProperty.call(responseJson, "items")
                ? responseJson.items
                : [responseJson],
              cart: cartContent,
              onSuccessDo
            }
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent("cart:change", {
            bubbles: true,
            detail: {
              baseEvent: "variant:add",
              onSuccessDo,
              cart: cartContent
            }
          })
        );
      } catch (error) {
        if (error?.name === "AbortError") return;

        // A failed fetch can be ambiguous: the request may have reached Shopify
        // even when the browser never received the response. Do not encourage an
        // immediate retry that could duplicate the line item; refresh cart state
        // and ask the shopper to verify the cart first.
        if (!addSucceeded) {
          dispatchCartError(
            form,
            "We couldn't confirm whether this item was added. Check your cart before trying again."
          );
        }
      } finally {
        requestControllers.delete(form);
        form.hzCartRequestPending = false;
        button?.removeAttribute("aria-busy");
        document.documentElement.dispatchEvent(
          new CustomEvent("theme:loading:end", { bubbles: true })
        );
      }
    };

    const handleFormSubmit = (event) => {
      const form = event.target.closest?.(".hz__productCardForm");
      if (!form || !root.contains(form)) return;
      event.preventDefault();
      const card = form.closest("[data-hz-product-card]");
      if (!card) return;
      submitCardForm(form, card);
    };

    const handleVariantAdd = (event) => {
      const card = event.target.closest?.("[data-hz-product-card]");
      if (!card || !root.contains(card)) return;

      clearCardStatus(card);
      const labels = labelsFor(card);
      const label = getCardButtonLabel(card);
      if (label) label.textContent = labels.added;

      if (activeModalCard === card) {
        if (modalAddLabel) modalAddLabel.textContent = labels.added;
        closeModal({ immediate: true });
      }

      window.setTimeout(() => {
        if (destroyed || !card.isConnected) return;
        const data = parseProductData(card);
        const representative = getRepresentativeVariant(card, data);
        setCardButtonState(card, representative, { preserveBusy: false });
      }, 850);
    };

    const handleCartError = (event) => {
      const card = event.target.closest?.("[data-hz-product-card]");
      if (!card || !root.contains(card)) return;

      const message = event.detail?.error || "Unable to add this item to the cart.";
      const data = parseProductData(card);
      const representative = activeModalCard === card && activeVariant
        ? activeVariant
        : getRepresentativeVariant(card, data);

      setCardButtonState(card, representative, { preserveBusy: false });

      if (activeModalCard === card && modal?.open) {
        clearCardStatus(card);
        setModalBusy(false);
        if (modalAdd) modalAdd.disabled = !activeVariant?.available || Boolean(quantityOneConstraint(activeVariant));
        if (modalAddLabel) {
          const labels = labelsFor(card);
          modalAddLabel.textContent = activeVariant?.available ? labels.add : labels.soldOut;
        }
        setModalStatus(message);
        setModalProductLink(activeModalData, true);
      } else {
        showCardStatus(card, message);
      }
    };

    const handleModalCancel = (event) => {
      event.preventDefault();
      closeModal();
    };

    const cardHoverBindings = cards.map((card) => {
      const image = getCardImage(card);
      const media = getCardMedia(card);
      if (image) rememberBaseImage(card, image.currentSrc || image.src, image.alt || "");

      const enter = () => startHover(card);
      const leave = () => stopHover(card);
      media?.addEventListener("mouseenter", enter);
      media?.addEventListener("mouseleave", leave);
      return { card, media, enter, leave };
    });

    if (hzProductCardFineHover.matches && "IntersectionObserver" in window) {
      hoverPrimeObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const card = entry.target.closest("[data-hz-product-card]");
            if (card) primeHoverMedia(card);
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: "240px 0px" }
      );
      cardHoverBindings.forEach(({ media }) => {
        if (media) hoverPrimeObserver.observe(media);
      });
    } else if (hzProductCardFineHover.matches) {
      cards.slice(0, 4).forEach(primeHoverMedia);
    }

    root.addEventListener("click", handleRootClick);
    root.addEventListener("submit", handleFormSubmit);
    root.addEventListener("variant:add", handleVariantAdd);
    root.addEventListener("cart:error", handleCartError);
    modalAdd?.addEventListener("click", handleModalAdd);
    modal?.addEventListener("cancel", handleModalCancel);

    hzProductCardControllers.set(root, {
      destroy() {
        destroyed = true;
        if (modalCloseTimer) window.clearTimeout(modalCloseTimer);
        hoverPrimeObserver?.disconnect();
        hoverPrimeObserver = null;
        requestControllers.forEach((controller) => controller.abort());
        requestControllers.clear();
        cards.forEach((card) => {
          stopHover(card, { restore: false });
          settleImageTransition(card, { commitVisible: true, resolveValue: false });
        });
        cardHoverBindings.forEach(({ media, enter, leave }) => {
          media?.removeEventListener("mouseenter", enter);
          media?.removeEventListener("mouseleave", leave);
        });
        root.removeEventListener("click", handleRootClick);
        root.removeEventListener("submit", handleFormSubmit);
        root.removeEventListener("variant:add", handleVariantAdd);
        root.removeEventListener("cart:error", handleCartError);
        modalAdd?.removeEventListener("click", handleModalAdd);
        modal?.removeEventListener("cancel", handleModalCancel);
        if (modal?.open) modal.close();
        hzProductCardControllers.delete(root);
      }
    });
  }

  function hzProductCardInitWithin(scope = document) {
    if (scope.matches?.("[data-hz-product-card-scope]")) hzProductCardCreate(scope);
    scope.querySelectorAll?.("[data-hz-product-card-scope]").forEach(hzProductCardCreate);
  }

  function hzProductCardDestroyWithin(scope) {
    const roots = [];
    if (scope.matches?.("[data-hz-product-card-scope]")) roots.push(scope);
    scope.querySelectorAll?.("[data-hz-product-card-scope]").forEach((root) => roots.push(root));
    roots.forEach((root) => hzProductCardControllers.get(root)?.destroy());
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => hzProductCardInitWithin(document), { once: true });
  } else {
    hzProductCardInitWithin(document);
  }

  document.addEventListener("shopify:section:load", (event) => hzProductCardInitWithin(event.target));
  document.addEventListener("shopify:section:unload", (event) => hzProductCardDestroyWithin(event.target));
})();

/* --------------------------------------------------------------------------
   AR Product Carousel HP — carousel navigation only
   -------------------------------------------------------------------------- */

(() => {
  "use strict";

  const hzProductCarouselHpControllers = new WeakMap();
  const hzProductCarouselHpReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  function hzProductCarouselHpCreate(root) {
    if (!root || hzProductCarouselHpControllers.has(root)) return;

    const track = root.querySelector("[data-hz-product-carousel-track]");
    const slides = Array.from(root.querySelectorAll("[data-hz-product-carousel-slide]"));
    const prev = root.querySelector("[data-hz-product-carousel-prev]");
    const next = root.querySelector("[data-hz-product-carousel-next]");
    const status = root.querySelector("[data-hz-product-carousel-status]");
    const progress = root.querySelector("[data-hz-product-carousel-progress]");
    const progressIndicator = root.querySelector("[data-hz-product-carousel-progress-indicator]");
    if (!track || !slides.length) return;

    let rafId = null;
    let resizeObserver = null;
    let destroyed = false;

    const getNearestIndex = () => {
      const trackRect = track.getBoundingClientRect();
      const anchor = trackRect.left;
      let nearestIndex = 0;
      let nearestDistance = Infinity;
      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - anchor);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });
      return nearestIndex;
    };

    const getBoundaryState = () => {
      const tolerance = 2;
      const trackRect = track.getBoundingClientRect();
      const firstRect = slides[0]?.getBoundingClientRect();
      const lastRect = slides[slides.length - 1]?.getBoundingClientRect();
      const styles = window.getComputedStyle(track);
      const paddingLeft = parseFloat(styles.paddingLeft) || 0;
      const paddingRight = parseFloat(styles.paddingRight) || 0;
      const viewportLeft = trackRect.left + paddingLeft;
      const viewportRight = trackRect.right - paddingRight;
      const isRtl = styles.direction === "rtl";
      const noOverflow = track.scrollWidth <= track.clientWidth + tolerance;

      if (!firstRect || !lastRect || noOverflow) return { atStart: true, atEnd: true };
      if (isRtl) {
        return {
          atStart: firstRect.right >= viewportRight - tolerance,
          atEnd: lastRect.left >= viewportLeft - tolerance
        };
      }
      return {
        atStart: firstRect.left >= viewportLeft - tolerance,
        atEnd: lastRect.right <= viewportRight + tolerance
      };
    };

    const setProgress = (nextIndex) => {
      if (!progress || !progressIndicator) return;
      const count = slides.length;
      const current = Math.max(1, Math.min(count, nextIndex + 1));
      const fill = count > 0 ? current / count : 0;
      progress.setAttribute("aria-valuenow", String(current));
      progress.setAttribute("aria-valuetext", `Slide ${current} of ${count}`);
      progressIndicator.style.transform = `scaleX(${fill.toFixed(6)})`;
    };

    const updateNav = () => {
      if (destroyed) return;
      const index = getNearestIndex();
      const { atStart, atEnd } = getBoundaryState();
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
      const progressIndex = atEnd ? slides.length - 1 : atStart ? 0 : index;
      setProgress(progressIndex);
      if (status) status.textContent = `Product ${progressIndex + 1} of ${slides.length}`;
    };

    const scrollToIndex = (index, behavior = "smooth") => {
      const safeIndex = Math.max(0, Math.min(index, slides.length - 1));
      slides[safeIndex]?.scrollIntoView({
        behavior: hzProductCarouselHpReducedMotion.matches ? "auto" : behavior,
        block: "nearest",
        inline: "start"
      });
    };

    const handleKeydown = (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      scrollToIndex(getNearestIndex() + (event.key === "ArrowRight" ? 1 : -1));
    };
    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        updateNav();
      });
    };
    const handlePrevClick = () => scrollToIndex(getNearestIndex() - 1);
    const handleNextClick = () => scrollToIndex(getNearestIndex() + 1);

    prev?.addEventListener("click", handlePrevClick);
    next?.addEventListener("click", handleNextClick);
    track.addEventListener("keydown", handleKeydown);
    track.addEventListener("scroll", handleScroll, { passive: true });
    track.addEventListener("scrollend", updateNav);

    if ("ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(updateNav);
      resizeObserver.observe(track);
    } else {
      window.addEventListener("resize", updateNav);
    }

    requestAnimationFrame(updateNav);

    hzProductCarouselHpControllers.set(root, {
      destroy() {
        destroyed = true;
        if (rafId !== null) cancelAnimationFrame(rafId);
        resizeObserver?.disconnect();
        if (!resizeObserver) window.removeEventListener("resize", updateNav);
        prev?.removeEventListener("click", handlePrevClick);
        next?.removeEventListener("click", handleNextClick);
        track.removeEventListener("keydown", handleKeydown);
        track.removeEventListener("scroll", handleScroll);
        track.removeEventListener("scrollend", updateNav);
        hzProductCarouselHpControllers.delete(root);
      }
    });
  }

  function hzProductCarouselHpInitWithin(scope = document) {
    if (scope.matches?.("[data-hz-product-carousel]")) hzProductCarouselHpCreate(scope);
    scope.querySelectorAll?.("[data-hz-product-carousel]").forEach(hzProductCarouselHpCreate);
  }

  function hzProductCarouselHpDestroyWithin(scope) {
    const roots = [];
    if (scope.matches?.("[data-hz-product-carousel]")) roots.push(scope);
    scope.querySelectorAll?.("[data-hz-product-carousel]").forEach((root) => roots.push(root));
    roots.forEach((root) => hzProductCarouselHpControllers.get(root)?.destroy());
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => hzProductCarouselHpInitWithin(document), { once: true });
  } else {
    hzProductCarouselHpInitWithin(document);
  }
  document.addEventListener("shopify:section:load", (event) => hzProductCarouselHpInitWithin(event.target));
  document.addEventListener("shopify:section:unload", (event) => hzProductCarouselHpDestroyWithin(event.target));
})();

/* --------------------------------------------------------------------------
   AR Product Tabs HP — category navigation / slider only
   -------------------------------------------------------------------------- */

(() => {
  "use strict";

  const hzProductTabsHpControllers = new WeakMap();
  const hzProductTabsHpReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  function hzProductTabsHpCreate(root) {
    if (!root || hzProductTabsHpControllers.has(root)) return;

    const tabsViewport = root.querySelector("[data-hz-product-tabs-viewport]");
    const tabsInner = root.querySelector("[data-hz-product-tabs-inner]");
    const indicator = root.querySelector("[data-hz-product-tabs-indicator]");
    const tabs = Array.from(root.querySelectorAll("[data-hz-product-tab]"));
    const track = root.querySelector("[data-hz-product-tabs-track]");
    const slides = Array.from(root.querySelectorAll("[data-hz-product-tabs-slide]"));
    const progress = root.querySelector("[data-hz-product-carousel-progress]");
    const progressIndicator = root.querySelector("[data-hz-product-carousel-progress-indicator]");
    const status = root.querySelector("[data-hz-product-tabs-status]");
    const behavior = root.dataset.hzProductTabsBehavior || "continuous";

    if (!tabsViewport || !tabsInner || !indicator || !tabs.length || !track || !slides.length) return;

    let rafId = null;
    let resizeObserver = null;
    let destroyed = false;
    let activeKey =
      tabs.find((tab) => tab.getAttribute("aria-selected") === "true" && !tab.disabled)?.dataset.hzCategoryKey ||
      tabs.find((tab) => !tab.disabled)?.dataset.hzCategoryKey ||
      "all";
    const scrollPositions = new Map();
    let tabScrollLock = false;
    let tabScrollUnlockTimer = null;
    let dragPointerId = null;
    let dragStartX = 0;
    let dragStartScrollLeft = 0;
    let dragMoved = false;
    let suppressClickUntil = 0;

    const getVisibleSlides = () => slides.filter((slide) => !slide.hidden);

    const getNearestVisibleIndex = () => {
      const visible = getVisibleSlides();
      if (!visible.length) return 0;
      const trackRect = track.getBoundingClientRect();
      const anchor = trackRect.left;
      let nearestIndex = 0;
      let nearestDistance = Infinity;
      visible.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - anchor);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });
      return nearestIndex;
    };

    const getBoundaryState = () => {
      const visible = getVisibleSlides();
      const tolerance = 2;
      const trackRect = track.getBoundingClientRect();
      const firstRect = visible[0]?.getBoundingClientRect();
      const lastRect = visible[visible.length - 1]?.getBoundingClientRect();
      const styles = window.getComputedStyle(track);
      const paddingLeft = parseFloat(styles.paddingLeft) || 0;
      const paddingRight = parseFloat(styles.paddingRight) || 0;
      const viewportLeft = trackRect.left + paddingLeft;
      const viewportRight = trackRect.right - paddingRight;
      const noOverflow = track.scrollWidth <= track.clientWidth + tolerance;
      if (!firstRect || !lastRect || noOverflow) return { atStart: true, atEnd: true, noOverflow };
      return {
        atStart: firstRect.left >= viewportLeft - tolerance,
        atEnd: lastRect.right <= viewportRight + tolerance,
        noOverflow
      };
    };

    const setProgress = (nextIndex) => {
      if (!progress || !progressIndicator) return;
      const visible = getVisibleSlides();
      const count = visible.length;
      const { noOverflow } = getBoundaryState();
      progress.hidden = count <= 1 || noOverflow;
      if (!count) {
        progress.setAttribute("aria-valuemin", "0");
        progress.setAttribute("aria-valuemax", "0");
        progress.setAttribute("aria-valuenow", "0");
        progress.setAttribute("aria-valuetext", "No products");
        progressIndicator.style.transform = "scaleX(0)";
        return;
      }
      const current = Math.max(1, Math.min(count, nextIndex + 1));
      progress.setAttribute("aria-valuemin", "1");
      progress.setAttribute("aria-valuemax", String(count));
      progress.setAttribute("aria-valuenow", String(current));
      progress.setAttribute("aria-valuetext", `Slide ${current} of ${count}`);
      progressIndicator.style.transform = `scaleX(${(current / count).toFixed(6)})`;
    };

    const positionIndicator = (animate = true) => {
      const activeTab = tabs.find((tab) => tab.dataset.hzCategoryKey === activeKey && !tab.disabled);
      if (!activeTab) return;
      if (!animate) indicator.style.transition = "none";
      indicator.style.width = `${activeTab.offsetWidth}px`;
      indicator.style.transform = `translate3d(${activeTab.offsetLeft}px, 0, 0)`;
      if (!animate) requestAnimationFrame(() => indicator.style.removeProperty("transition"));
    };

    const centerActiveTab = (tab) => {
      const desired = tab.offsetLeft - (tabsViewport.clientWidth - tab.offsetWidth) / 2;
      const max = Math.max(0, tabsViewport.scrollWidth - tabsViewport.clientWidth);
      tabsViewport.scrollTo({
        left: Math.max(0, Math.min(max, desired)),
        behavior: hzProductTabsHpReducedMotion.matches ? "auto" : "smooth"
      });
    };

    const updateTabSeparators = () => {
      tabsInner.querySelectorAll(".hz__productTabsHpTabSeparator").forEach((separator) => {
        const previousTab = separator.previousElementSibling;
        const nextTab = separator.nextElementSibling;
        const touchesActiveTab =
          previousTab?.matches?.('[data-hz-product-tab][aria-selected="true"]') ||
          nextTab?.matches?.('[data-hz-product-tab][aria-selected="true"]');
        separator.classList.toggle("hz__productTabsHpTabSeparator--concealed", Boolean(touchesActiveTab));
      });
    };

    const setActiveTab = (key, { center = true, animate = true } = {}) => {
      const target = tabs.find((tab) => tab.dataset.hzCategoryKey === key && !tab.disabled);
      if (!target) return;
      activeKey = key;
      tabs.forEach((tab) => {
        const selected = tab === target;
        tab.setAttribute("aria-selected", selected ? "true" : "false");
        tab.tabIndex = selected ? 0 : -1;
      });
      updateTabSeparators();
      positionIndicator(animate);
      if (center) centerActiveTab(target);
    };

    const scrollToVisibleIndex = (index, scrollBehavior = "smooth") => {
      const visible = getVisibleSlides();
      const safeIndex = Math.max(0, Math.min(index, visible.length - 1));
      const slide = visible[safeIndex];
      if (!slide) return;
      track.scrollTo({
        left: slide.offsetLeft,
        behavior: hzProductTabsHpReducedMotion.matches ? "auto" : scrollBehavior
      });
    };

    const syncActiveTabFromRail = () => {
      if (behavior !== "continuous" || tabScrollLock) return;
      const slide = getVisibleSlides()[getNearestVisibleIndex()];
      const key = slide?.dataset.hzCategoryKey;
      if (key && key !== activeKey) setActiveTab(key);
    };

    const updateRailState = () => {
      if (destroyed) return;
      const visible = getVisibleSlides();
      if (!visible.length) {
        setProgress(0);
        if (status) status.textContent = "No products";
        return;
      }
      const index = getNearestVisibleIndex();
      const { atStart, atEnd } = getBoundaryState();
      const progressIndex = atEnd ? visible.length - 1 : atStart ? 0 : index;
      setProgress(progressIndex);
      syncActiveTabFromRail();
      if (status) status.textContent = `Product ${progressIndex + 1} of ${visible.length}`;
    };

    const storeCurrentScrollPosition = () => scrollPositions.set(activeKey, track.scrollLeft);

    const applySeparateCategory = (key, restorePosition = true) => {
      slides.forEach((slide) => {
        slide.hidden = key !== "all" && slide.dataset.hzCategoryKey !== key;
      });
      const nextScroll = restorePosition ? scrollPositions.get(key) || 0 : 0;
      requestAnimationFrame(() => {
        track.scrollLeft = Math.max(0, Math.min(nextScroll, Math.max(0, track.scrollWidth - track.clientWidth)));
        updateRailState();
      });
    };

    const unlockProgrammaticTabScroll = () => {
      tabScrollLock = false;
      if (tabScrollUnlockTimer) window.clearTimeout(tabScrollUnlockTimer);
      tabScrollUnlockTimer = null;
    };

    const activateTab = (key, options = {}) => {
      const targetTab = tabs.find((tab) => tab.dataset.hzCategoryKey === key && !tab.disabled);
      if (!targetTab) return;
      if (behavior === "separate") {
        storeCurrentScrollPosition();
        setActiveTab(key, options);
        applySeparateCategory(key);
        return;
      }
      setActiveTab(key, options);
      tabScrollLock = true;
      if (tabScrollUnlockTimer) window.clearTimeout(tabScrollUnlockTimer);
      const targetSlide = key === "all" ? slides[0] : slides.find((slide) => slide.dataset.hzCategoryKey === key);
      if (targetSlide) {
        track.scrollTo({
          left: targetSlide.offsetLeft,
          behavior: hzProductTabsHpReducedMotion.matches ? "auto" : "smooth"
        });
      }
      tabScrollUnlockTimer = window.setTimeout(unlockProgrammaticTabScroll, hzProductTabsHpReducedMotion.matches ? 0 : 750);
    };

    const handleTabClick = (event) => {
      const tab = event.target.closest("[data-hz-product-tab]");
      if (!tab || tab.disabled || !tabsViewport.contains(tab)) return;
      activateTab(tab.dataset.hzCategoryKey);
    };

    const handleTabsKeydown = (event) => {
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
      const enabledTabs = tabs.filter((tab) => !tab.disabled);
      if (!enabledTabs.length) return;
      const currentIndex = Math.max(0, enabledTabs.findIndex((tab) => tab.dataset.hzCategoryKey === activeKey));
      let nextIndex = currentIndex;
      if (event.key === "ArrowLeft") nextIndex = Math.max(0, currentIndex - 1);
      if (event.key === "ArrowRight") nextIndex = Math.min(enabledTabs.length - 1, currentIndex + 1);
      if (event.key === "Home") nextIndex = 0;
      if (event.key === "End") nextIndex = enabledTabs.length - 1;
      event.preventDefault();
      const target = enabledTabs[nextIndex];
      target.focus();
      activateTab(target.dataset.hzCategoryKey);
    };

    const handleTrackKeydown = (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      scrollToVisibleIndex(getNearestVisibleIndex() + (event.key === "ArrowRight" ? 1 : -1));
    };
    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        updateRailState();
      });
    };
    const handleScrollEnd = () => {
      updateRailState();
      unlockProgrammaticTabScroll();
    };

    const handlePointerDown = (event) => {
      if (event.pointerType !== "mouse" || event.button !== 0) return;
      if (event.target.closest("button, input, select, textarea, [role='dialog'], [data-hz-product-modal]")) return;
      dragPointerId = event.pointerId;
      dragStartX = event.clientX;
      dragStartScrollLeft = track.scrollLeft;
      dragMoved = false;
      track.setPointerCapture?.(dragPointerId);
    };
    const handlePointerMove = (event) => {
      if (dragPointerId !== event.pointerId) return;
      const delta = event.clientX - dragStartX;
      if (!dragMoved && Math.abs(delta) < 5) return;
      dragMoved = true;
      track.classList.add("hz__productTabsHpTrack--dragging");
      track.scrollLeft = dragStartScrollLeft - delta;
      event.preventDefault();
    };
    const finishPointerDrag = (event) => {
      if (dragPointerId !== event.pointerId) return;
      if (dragMoved) suppressClickUntil = performance.now() + 180;
      track.classList.remove("hz__productTabsHpTrack--dragging");
      try { track.releasePointerCapture?.(dragPointerId); } catch (_) {}
      dragPointerId = null;
      dragMoved = false;
      updateRailState();
    };
    const handleTrackClickCapture = (event) => {
      if (performance.now() >= suppressClickUntil) return;
      event.preventDefault();
      event.stopPropagation();
    };

    const selectBlock = (blockId) => {
      const tab = tabs.find((item) => item.dataset.hzProductTabsBlockId === blockId);
      if (!tab || tab.disabled) return;
      activateTab(tab.dataset.hzCategoryKey, { center: true, animate: !hzProductTabsHpReducedMotion.matches });
    };

    tabsViewport.addEventListener("click", handleTabClick);
    tabsViewport.addEventListener("keydown", handleTabsKeydown);
    track.addEventListener("keydown", handleTrackKeydown);
    track.addEventListener("scroll", handleScroll, { passive: true });
    track.addEventListener("scrollend", handleScrollEnd);
    track.addEventListener("pointerdown", handlePointerDown);
    track.addEventListener("pointermove", handlePointerMove);
    track.addEventListener("pointerup", finishPointerDrag);
    track.addEventListener("pointercancel", finishPointerDrag);
    track.addEventListener("click", handleTrackClickCapture, true);

    if ("ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(() => {
        positionIndicator(false);
        updateRailState();
      });
      resizeObserver.observe(root);
    } else {
      window.addEventListener("resize", updateRailState);
    }

    if (behavior === "separate") applySeparateCategory(activeKey, false);
    else slides.forEach((slide) => { slide.hidden = false; });

    requestAnimationFrame(() => {
      positionIndicator(false);
      tabsInner.classList.add("hz__productTabsHpTabsInner--ready");
      setActiveTab(activeKey, { center: false, animate: false });
      updateRailState();
    });

    hzProductTabsHpControllers.set(root, {
      selectBlock,
      destroy() {
        destroyed = true;
        if (rafId !== null) cancelAnimationFrame(rafId);
        if (tabScrollUnlockTimer) window.clearTimeout(tabScrollUnlockTimer);
        resizeObserver?.disconnect();
        if (!resizeObserver) window.removeEventListener("resize", updateRailState);
        tabsViewport.removeEventListener("click", handleTabClick);
        tabsViewport.removeEventListener("keydown", handleTabsKeydown);
        track.removeEventListener("keydown", handleTrackKeydown);
        track.removeEventListener("scroll", handleScroll);
        track.removeEventListener("scrollend", handleScrollEnd);
        track.removeEventListener("pointerdown", handlePointerDown);
        track.removeEventListener("pointermove", handlePointerMove);
        track.removeEventListener("pointerup", finishPointerDrag);
        track.removeEventListener("pointercancel", finishPointerDrag);
        track.removeEventListener("click", handleTrackClickCapture, true);
        hzProductTabsHpControllers.delete(root);
      }
    });
  }

  function hzProductTabsHpInitWithin(scope = document) {
    if (scope.matches?.("[data-hz-product-tabs]")) hzProductTabsHpCreate(scope);
    scope.querySelectorAll?.("[data-hz-product-tabs]").forEach(hzProductTabsHpCreate);
  }

  function hzProductTabsHpDestroyWithin(scope) {
    const roots = [];
    if (scope.matches?.("[data-hz-product-tabs]")) roots.push(scope);
    scope.querySelectorAll?.("[data-hz-product-tabs]").forEach((root) => roots.push(root));
    roots.forEach((root) => hzProductTabsHpControllers.get(root)?.destroy());
  }

  function hzProductTabsHpHandleBlockSelect(event) {
    const blockElement = event.target.closest?.("[data-hz-product-tabs-block-id]");
    const root = blockElement?.closest?.("[data-hz-product-tabs]");
    if (!blockElement || !root) return;
    hzProductTabsHpControllers.get(root)?.selectBlock(blockElement.dataset.hzProductTabsBlockId);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => hzProductTabsHpInitWithin(document), { once: true });
  } else {
    hzProductTabsHpInitWithin(document);
  }
  document.addEventListener("shopify:section:load", (event) => hzProductTabsHpInitWithin(event.target));
  document.addEventListener("shopify:section:unload", (event) => hzProductTabsHpDestroyWithin(event.target));
  document.addEventListener("shopify:block:select", hzProductTabsHpHandleBlockSelect);
})();


/* --------------------------------------------------------------------------
   AR Announcement Bar
   -------------------------------------------------------------------------- */

(() => {
  "use strict";

  const hzAnnouncementBarControllers = new WeakMap();
  const hzAnnouncementBarReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  );

  function hzAnnouncementBarCreate(root) {
    if (!root || hzAnnouncementBarControllers.has(root)) return;

    const mode = root.dataset.hzAnnouncementMode || "slideshow";
    const slides = Array.from(
      root.querySelectorAll("[data-hz-announcement-slide]")
    );
    const status = root.querySelector("[data-hz-announcement-status]");

    if (mode !== "slideshow" || slides.length < 2) {
      hzAnnouncementBarControllers.set(root, {
        destroy() {
          hzAnnouncementBarControllers.delete(root);
        }
      });
      return;
    }

    const delay = Math.max(
      2000,
      Number(root.dataset.hzAnnouncementDelay) || 5000
    );

    let activeIndex = Math.max(
      0,
      slides.findIndex((slide) =>
        slide.classList.contains("hz__announcementBarSlide--active")
      )
    );
    let timerId = null;
    let transitionFallbackId = null;
    let transitionFrameId = null;
    let transitioning = false;
    let destroyed = false;

    function clearTimer() {
      if (timerId !== null) {
        window.clearTimeout(timerId);
        timerId = null;
      }
    }

    function clearTransitionFallback() {
      if (transitionFallbackId !== null) {
        window.clearTimeout(transitionFallbackId);
        transitionFallbackId = null;
      }
    }

    function clearTransitionFrame() {
      if (transitionFrameId !== null) {
        window.cancelAnimationFrame(transitionFrameId);
        transitionFrameId = null;
      }
    }

    function scheduleNext() {
      clearTimer();

      if (
        destroyed ||
        hzAnnouncementBarReducedMotion.matches ||
        document.hidden
      ) {
        return;
      }

      timerId = window.setTimeout(() => {
        showSlide((activeIndex + 1) % slides.length);
      }, delay);
    }

    function cleanupClasses(slide) {
      slide.classList.remove(
        "hz__announcementBarSlide--incoming",
        "hz__announcementBarSlide--leaving"
      );
    }

    function finalize(nextIndex) {
      clearTransitionFallback();
      clearTransitionFrame();

      slides.forEach((slide, index) => {
        const active = index === nextIndex;

        cleanupClasses(slide);
        slide.classList.toggle("hz__announcementBarSlide--active", active);
        slide.setAttribute("aria-hidden", active ? "false" : "true");

        if (active) {
          slide.removeAttribute("inert");
        } else {
          slide.setAttribute("inert", "");
        }
      });

      activeIndex = nextIndex;
      transitioning = false;

      if (status) {
        status.textContent = `Announcement ${activeIndex + 1} of ${slides.length}`;
      }

      scheduleNext();
    }

    function showSlide(nextIndex) {
      if (
        destroyed ||
        transitioning ||
        nextIndex === activeIndex
      ) {
        scheduleNext();
        return;
      }

      if (hzAnnouncementBarReducedMotion.matches) {
        finalize(nextIndex);
        return;
      }

      const outgoing = slides[activeIndex];
      const incoming = slides[nextIndex];

      transitioning = true;
      clearTimer();

      cleanupClasses(outgoing);
      cleanupClasses(incoming);

      incoming.classList.add("hz__announcementBarSlide--incoming");
      incoming.setAttribute("aria-hidden", "false");
      incoming.removeAttribute("inert");

      outgoing.setAttribute("aria-hidden", "true");
      outgoing.setAttribute("inert", "");

      incoming.getBoundingClientRect();

      transitionFrameId = window.requestAnimationFrame(() => {
        transitionFrameId = null;
        if (destroyed) return;

        outgoing.classList.remove("hz__announcementBarSlide--active");
        outgoing.classList.add("hz__announcementBarSlide--leaving");

        incoming.classList.remove("hz__announcementBarSlide--incoming");
        incoming.classList.add("hz__announcementBarSlide--active");

        const handleTransitionEnd = (event) => {
          if (
            event.target !== incoming ||
            event.propertyName !== "opacity"
          ) {
            return;
          }

          incoming.removeEventListener(
            "transitionend",
            handleTransitionEnd
          );
          finalize(nextIndex);
        };

        incoming.addEventListener(
          "transitionend",
          handleTransitionEnd
        );

        transitionFallbackId = window.setTimeout(() => {
          incoming.removeEventListener(
            "transitionend",
            handleTransitionEnd
          );
          finalize(nextIndex);
        }, 480);
      });
    }

    function handleVisibilityChange() {
      if (document.hidden) {
        clearTimer();
      } else {
        scheduleNext();
      }
    }

    function handleReducedMotionChange() {
      if (hzAnnouncementBarReducedMotion.matches) {
        clearTimer();
      } else {
        scheduleNext();
      }
    }

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );
    hzAnnouncementBarReducedMotion.addEventListener(
      "change",
      handleReducedMotionChange
    );

    scheduleNext();

    hzAnnouncementBarControllers.set(root, {
      destroy() {
        destroyed = true;
        clearTimer();
        clearTransitionFallback();
        clearTransitionFrame();

        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        hzAnnouncementBarReducedMotion.removeEventListener(
          "change",
          handleReducedMotionChange
        );

        hzAnnouncementBarControllers.delete(root);
      }
    });
  }

  function hzAnnouncementBarInitWithin(scope = document) {
    if (scope.matches?.("[data-hz-announcement-bar]")) {
      hzAnnouncementBarCreate(scope);
    }

    scope
      .querySelectorAll?.("[data-hz-announcement-bar]")
      .forEach(hzAnnouncementBarCreate);
  }

  function hzAnnouncementBarDestroyWithin(scope) {
    const roots = [];

    if (scope.matches?.("[data-hz-announcement-bar]")) {
      roots.push(scope);
    }

    scope
      .querySelectorAll?.("[data-hz-announcement-bar]")
      .forEach((root) => roots.push(root));

    roots.forEach((root) => {
      hzAnnouncementBarControllers.get(root)?.destroy();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => hzAnnouncementBarInitWithin(document),
      { once: true }
    );
  } else {
    hzAnnouncementBarInitWithin(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    hzAnnouncementBarInitWithin(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    hzAnnouncementBarDestroyWithin(event.target);
  });
})();

/* --------------------------------------------------------------------------
   AR Header
   Prestige account/search/cart integration + custom mobile drawer/sticky UX.
   -------------------------------------------------------------------------- */

(() => {
  "use strict";

  const hzHeaderControllers = new WeakMap();
  const hzHeaderReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  );

  function hzHeaderCreate(root) {
    if (!root || hzHeaderControllers.has(root)) return;

    const stickyMode = root.dataset.hzHeaderSticky || "always";
    const sectionElement =
      root.closest(".hz__headerSection") || root.closest(".shopify-section");
    const headerElement = root.querySelector(".hz__header");
    const drawer = root.querySelector("[data-hz-header-drawer]");
    const openMenuButton = root.querySelector("[data-hz-header-menu-open]");
    const closeMenuButton = root.querySelector("[data-hz-header-menu-close]");
    const searchElement = root.querySelector(".hz__headerSearch");
    const searchInput = root.querySelector("[data-hz-header-search-input]");
    const searchDefault = root.querySelector("[data-hz-header-search-default]");
    const searchResults = root.querySelector("[data-hz-header-search-results]");
    const cartBadges = Array.from(
      root.querySelectorAll("[data-hz-header-cart-badge]")
    );
    const cartCounts = Array.from(
      root.querySelectorAll("[data-hz-header-cart-count]")
    );
    const accountElements = Array.from(
      root.querySelectorAll(".hz__headerAccount")
    );
    const navItems = Array.from(
      root.querySelectorAll("[data-hz-header-nav-item]")
    );
    const flyoutItems = Array.from(
      root.querySelectorAll("[data-hz-header-flyout-item]")
    );
    const mobileNav = root.querySelector("[data-hz-header-mobile-nav]");
    const mobileNavPanels = Array.from(
      root.querySelectorAll("[data-hz-header-mobile-nav-panel]")
    );
    const mobileDrillButtons = Array.from(
      root.querySelectorAll("[data-hz-header-mobile-drill]")
    );
    const mobileBackButtons = Array.from(
      root.querySelectorAll("[data-hz-header-mobile-back]")
    );
    const mobilePanelMap = new Map(
      mobileNavPanels.map((panel) => [panel.dataset.hzHeaderMobilePanelKey, panel])
    );
    const desktopNavMedia = window.matchMedia("(min-width: 1024px)");
    const finePointerMedia = window.matchMedia(
      "(hover: hover) and (pointer: fine)"
    );
    const menuTriggerMode = root.dataset.hzHeaderMenuTrigger || "hover";
    const menuOpenDelay = Math.max(
      0,
      Number.parseInt(root.dataset.hzHeaderMenuOpenDelay || "350", 10) || 350
    );
    const menuCloseDelay = Math.max(
      0,
      Number.parseInt(root.dataset.hzHeaderMenuCloseDelay || "200", 10) || 200
    );
    const navOpenTimers = new Map();
    const navCloseTimers = new Map();
    const flyoutOpenTimers = new Map();
    const flyoutCloseTimers = new Map();
    const navAbortController = new AbortController();
    const documentRootStyle = document.documentElement.style;
    const previousThemeHeaderVariables = new Map(
      ["--header-height", "--header-is-sticky", "--header-is-visible"].map(
        (property) => [property, documentRootStyle.getPropertyValue(property)]
      )
    );

    let previousScrollY = Math.max(0, window.scrollY);
    let downwardTravel = 0;
    let upwardTravel = 0;
    let headerHidden = false;
    let accountSheetOpen = false;
    let drawerCloseTimer = null;
    let rafId = null;
    let resizeObserver = null;
    let mobilePanelStack = [{ key: "root", trigger: null, scrollTop: 0 }];

    if (navItems.length > 0) {
      root.classList.add("hz__headerNavEnhanced");
    }

    sectionElement?.classList.toggle(
      "hz__headerSection--sticky",
      stickyMode !== "none"
    );
    sectionElement?.classList.toggle(
      "hz__headerSection--reveal",
      stickyMode === "reveal"
    );

    function setHeaderHidden(hidden) {
      const shouldHide = stickyMode === "reveal" && Boolean(hidden);

      if (headerHidden === shouldHide) return;

      headerHidden = shouldHide;
      root.classList.toggle("hz__headerWrap--hidden", shouldHide);
      sectionElement?.classList.toggle(
        "hz__headerSection--hidden",
        shouldHide
      );
      documentRootStyle.setProperty(
        "--header-is-visible",
        shouldHide ? "0" : "1"
      );
    }

    function syncThemeHeaderMetrics() {
      const measuredHeight =
        headerElement?.getBoundingClientRect().height ||
        root.getBoundingClientRect().height;

      if (Number.isFinite(measuredHeight) && measuredHeight > 0) {
        documentRootStyle.setProperty(
          "--header-height",
          `${measuredHeight.toFixed(2)}px`
        );
      }

      documentRootStyle.setProperty(
        "--header-is-sticky",
        stickyMode === "none" ? "0" : "1"
      );
      documentRootStyle.setProperty(
        "--header-is-visible",
        headerHidden ? "0" : "1"
      );
    }


    function clearNavigationTimer(timerMap, item) {
      const timer = timerMap.get(item);

      if (timer !== undefined) {
        window.clearTimeout(timer);
        timerMap.delete(item);
      }
    }

    function clearNavigationTimersForItem(item) {
      clearNavigationTimer(navOpenTimers, item);
      clearNavigationTimer(navCloseTimers, item);
    }

    function clearFlyoutTimersForItem(item) {
      clearNavigationTimer(flyoutOpenTimers, item);
      clearNavigationTimer(flyoutCloseTimers, item);
    }

    function clearAllNavigationTimers() {
      [
        navOpenTimers,
        navCloseTimers,
        flyoutOpenTimers,
        flyoutCloseTimers
      ].forEach((timerMap) => {
        timerMap.forEach((timer) => window.clearTimeout(timer));
        timerMap.clear();
      });
    }

    function getNavToggle(item) {
      return item?.querySelector(
        ":scope > .hz__headerMenuTrigger [data-hz-header-nav-toggle]"
      );
    }

    function getNavPanel(item) {
      return item?.querySelector(":scope > [data-hz-header-nav-panel]");
    }

    function getFlyoutToggle(item) {
      return item?.querySelector(
        ":scope > .hz__headerDesktopSubmenuRow [data-hz-header-flyout-toggle]"
      );
    }

    function getFlyoutPanel(item) {
      return item?.querySelector(":scope > [data-hz-header-flyout-panel]");
    }

    function setPanelA11yState(panel, open) {
      if (!panel) return;
      panel.setAttribute("aria-hidden", open ? "false" : "true");
    }

    function positionDropdown(item) {
      if (!item || item.dataset.hzHeaderNavLayout !== "dropdown") return;

      const panel = getNavPanel(item);
      if (!panel) return;

      panel.style.setProperty("--hz-header-dropdown-shift-x", "0px");

      window.requestAnimationFrame(() => {
        if (item.dataset.hzHeaderNavOpen !== "true") return;

        const rect = panel.getBoundingClientRect();
        const viewportPadding = 12;
        let shift = 0;

        if (rect.left < viewportPadding) {
          shift += viewportPadding - rect.left;
        }

        if (rect.right + shift > window.innerWidth - viewportPadding) {
          shift -= rect.right + shift - (window.innerWidth - viewportPadding);
        }

        panel.style.setProperty(
          "--hz-header-dropdown-shift-x",
          `${Math.round(shift)}px`
        );
      });
    }

    function positionFlyout(item) {
      const panel = getFlyoutPanel(item);
      if (!panel) return;

      panel.removeAttribute("data-hz-header-flyout-side");
      panel.style.setProperty("--hz-header-flyout-shift-y", "0px");

      window.requestAnimationFrame(() => {
        if (item.dataset.hzHeaderFlyoutOpen !== "true") return;

        const rect = panel.getBoundingClientRect();
        const viewportPadding = 12;

        if (rect.right > window.innerWidth - viewportPadding) {
          panel.setAttribute("data-hz-header-flyout-side", "left");
        }

        let shiftY = 0;

        if (rect.bottom > window.innerHeight - viewportPadding) {
          shiftY -= rect.bottom - (window.innerHeight - viewportPadding);
        }

        if (rect.top + shiftY < viewportPadding) {
          shiftY += viewportPadding - (rect.top + shiftY);
        }

        panel.style.setProperty(
          "--hz-header-flyout-shift-y",
          `${Math.round(shiftY)}px`
        );
      });
    }

    function closeFlyoutItem(item, { focusToggle = false } = {}) {
      if (!item) return;

      clearFlyoutTimersForItem(item);
      item.removeAttribute("data-hz-header-flyout-open");

      const toggle = getFlyoutToggle(item);
      const panel = getFlyoutPanel(item);

      toggle?.setAttribute("aria-expanded", "false");
      setPanelA11yState(panel, false);

      if (focusToggle) {
        toggle?.focus({ preventScroll: true });
      }
    }

    function closeFlyoutsWithin(container, exceptItem = null) {
      flyoutItems.forEach((item) => {
        if (exceptItem === item) return;
        if (container && !container.contains(item)) return;
        closeFlyoutItem(item);
      });
    }

    function openFlyoutItem(item) {
      if (!item || !desktopNavMedia.matches) return;

      clearFlyoutTimersForItem(item);

      const parentPanel = item.closest("[data-hz-header-nav-panel]");
      closeFlyoutsWithin(parentPanel, item);

      item.setAttribute("data-hz-header-flyout-open", "true");

      const toggle = getFlyoutToggle(item);
      const panel = getFlyoutPanel(item);

      toggle?.setAttribute("aria-expanded", "true");
      setPanelA11yState(panel, true);
      positionFlyout(item);
    }

    function closeNavItem(item, { focusToggle = false } = {}) {
      if (!item) return;

      clearNavigationTimersForItem(item);
      item.removeAttribute("data-hz-header-nav-open");

      const toggle = getNavToggle(item);
      const panel = getNavPanel(item);

      toggle?.setAttribute("aria-expanded", "false");
      setPanelA11yState(panel, false);
      closeFlyoutsWithin(item);

      if (focusToggle) {
        toggle?.focus({ preventScroll: true });
      }
    }

    function closeAllNavItems(exceptItem = null) {
      navItems.forEach((item) => {
        if (item !== exceptItem) {
          closeNavItem(item);
        }
      });
    }

    function openNavItem(item) {
      if (!item || !desktopNavMedia.matches) return;

      clearNavigationTimersForItem(item);
      closeAllNavItems(item);

      item.setAttribute("data-hz-header-nav-open", "true");

      const toggle = getNavToggle(item);
      const panel = getNavPanel(item);

      toggle?.setAttribute("aria-expanded", "true");
      setPanelA11yState(panel, true);
      setHeaderHidden(false);
      positionDropdown(item);
    }

    function scheduleNavOpen(item) {
      if (
        menuTriggerMode !== "hover" ||
        !finePointerMedia.matches ||
        !desktopNavMedia.matches
      ) {
        return;
      }

      clearNavigationTimer(navCloseTimers, item);
      clearNavigationTimer(navOpenTimers, item);

      navOpenTimers.set(
        item,
        window.setTimeout(() => {
          navOpenTimers.delete(item);
          openNavItem(item);
        }, menuOpenDelay)
      );
    }

    function scheduleNavClose(item) {
      if (
        menuTriggerMode !== "hover" ||
        !finePointerMedia.matches ||
        !desktopNavMedia.matches
      ) {
        return;
      }

      clearNavigationTimer(navOpenTimers, item);
      clearNavigationTimer(navCloseTimers, item);

      navCloseTimers.set(
        item,
        window.setTimeout(() => {
          navCloseTimers.delete(item);

          if (!item.matches(":hover") && !item.matches(":focus-within")) {
            closeNavItem(item);
          }
        }, menuCloseDelay)
      );
    }

    function scheduleFlyoutOpen(item) {
      if (
        menuTriggerMode !== "hover" ||
        !finePointerMedia.matches ||
        !desktopNavMedia.matches
      ) {
        return;
      }

      clearNavigationTimer(flyoutCloseTimers, item);
      clearNavigationTimer(flyoutOpenTimers, item);

      flyoutOpenTimers.set(
        item,
        window.setTimeout(() => {
          flyoutOpenTimers.delete(item);
          openFlyoutItem(item);
        }, Math.min(menuOpenDelay, 300))
      );
    }

    function scheduleFlyoutClose(item) {
      if (
        menuTriggerMode !== "hover" ||
        !finePointerMedia.matches ||
        !desktopNavMedia.matches
      ) {
        return;
      }

      clearNavigationTimer(flyoutOpenTimers, item);
      clearNavigationTimer(flyoutCloseTimers, item);

      flyoutCloseTimers.set(
        item,
        window.setTimeout(() => {
          flyoutCloseTimers.delete(item);

          if (!item.matches(":hover") && !item.matches(":focus-within")) {
            closeFlyoutItem(item);
          }
        }, menuCloseDelay)
      );
    }

    function handleNavPointerEnter(event) {
      scheduleNavOpen(event.currentTarget);
    }

    function handleNavPointerLeave(event) {
      scheduleNavClose(event.currentTarget);
    }

    function handleNavFocusIn(event) {
      if (!desktopNavMedia.matches) return;

      const item = event.currentTarget;
      clearNavigationTimer(navCloseTimers, item);
    }

    function handleNavFocusOut(event) {
      const item = event.currentTarget;

      window.requestAnimationFrame(() => {
        if (!item.contains(document.activeElement) && !item.matches(":hover")) {
          closeNavItem(item);
        }
      });
    }

    function handleNavToggle(event) {
      const item = event.currentTarget.closest("[data-hz-header-nav-item]");
      if (!item || !desktopNavMedia.matches) return;

      if (item.dataset.hzHeaderNavOpen === "true") {
        closeNavItem(item);
      } else {
        openNavItem(item);
      }
    }

    function handleNavKeydown(event) {
      if (event.key !== "Escape") return;

      const item = event.currentTarget;
      if (item.dataset.hzHeaderNavOpen !== "true") return;

      event.preventDefault();
      event.stopPropagation();
      closeNavItem(item, { focusToggle: true });
    }

    function handleFlyoutPointerEnter(event) {
      scheduleFlyoutOpen(event.currentTarget);
    }

    function handleFlyoutPointerLeave(event) {
      scheduleFlyoutClose(event.currentTarget);
    }

    function handleFlyoutFocusIn(event) {
      if (!desktopNavMedia.matches) return;

      const item = event.currentTarget;
      clearNavigationTimer(flyoutCloseTimers, item);
    }

    function handleFlyoutFocusOut(event) {
      const item = event.currentTarget;

      window.requestAnimationFrame(() => {
        if (!item.contains(document.activeElement) && !item.matches(":hover")) {
          closeFlyoutItem(item);
        }
      });
    }

    function handleFlyoutToggle(event) {
      const item = event.currentTarget.closest("[data-hz-header-flyout-item]");
      if (!item || !desktopNavMedia.matches) return;

      if (item.dataset.hzHeaderFlyoutOpen === "true") {
        closeFlyoutItem(item);
      } else {
        openFlyoutItem(item);
      }
    }

    function handleFlyoutKeydown(event) {
      if (event.key !== "Escape") return;

      const item = event.currentTarget;
      if (item.dataset.hzHeaderFlyoutOpen !== "true") return;

      event.preventDefault();
      event.stopPropagation();
      closeFlyoutItem(item, { focusToggle: true });
    }

    function handleDocumentPointerDown(event) {
      if (!desktopNavMedia.matches) return;

      if (!event.target.closest("[data-hz-header-nav-item]")) {
        closeAllNavItems();
      }
    }

    function setMobilePanelA11yState(panel, open) {
      if (!panel) return;

      panel.setAttribute("aria-hidden", open ? "false" : "true");

      if ("inert" in panel) {
        panel.inert = !open;
      }
    }

    function renderMobilePanels({ focus = false, restoreScroll = true } = {}) {
      const activeEntry = mobilePanelStack[mobilePanelStack.length - 1];
      const activePanel = mobilePanelMap.get(activeEntry.key);

      mobileNav?.style.setProperty(
        "--hz-header-mobile-depth",
        String(Math.max(0, mobilePanelStack.length - 1))
      );

      mobileNavPanels.forEach((panel) => {
        const stackIndex = mobilePanelStack.findIndex(
          (entry) => entry.key === panel.dataset.hzHeaderMobilePanelKey
        );
        const isActive = panel === activePanel;
        const isPrevious = stackIndex > -1 && !isActive;

        panel.toggleAttribute("data-hz-header-mobile-panel-active", isActive);
        panel.toggleAttribute("data-hz-header-mobile-panel-previous", isPrevious);
        setMobilePanelA11yState(panel, isActive);
      });

      if (activePanel && restoreScroll) {
        activePanel.scrollTop = activeEntry.scrollTop || 0;
      }

      if (focus && activePanel) {
        window.requestAnimationFrame(() => {
          const focusTarget =
            activePanel.querySelector("[data-hz-header-mobile-back]") ||
            activePanel.querySelector("a, button");
          focusTarget?.focus({ preventScroll: true });
        });
      }
    }

    function resetMobilePanels() {
      mobilePanelStack = [{ key: "root", trigger: null, scrollTop: 0 }];
      renderMobilePanels({ restoreScroll: false });
      const rootPanel = mobilePanelMap.get("root");
      if (rootPanel) rootPanel.scrollTop = 0;
    }

    function openMobilePanel(key, trigger = null, { focus = true } = {}) {
      if (desktopNavMedia.matches || !mobilePanelMap.has(key)) return;

      const currentEntry = mobilePanelStack[mobilePanelStack.length - 1];
      const currentPanel = mobilePanelMap.get(currentEntry.key);
      if (currentPanel) currentEntry.scrollTop = currentPanel.scrollTop;

      const existingIndex = mobilePanelStack.findIndex((entry) => entry.key === key);
      if (existingIndex > -1) {
        mobilePanelStack = mobilePanelStack.slice(0, existingIndex + 1);
      } else {
        mobilePanelStack.push({ key, trigger, scrollTop: 0 });
      }

      renderMobilePanels({ focus });
    }

    function openMobilePanelPath(key, trigger = null) {
      const childMatch = /^child-(\d+)-(\d+)$/.exec(key);
      mobilePanelStack = [{ key: "root", trigger: null, scrollTop: 0 }];

      if (childMatch) {
        const parentKey = `top-${childMatch[1]}`;
        if (mobilePanelMap.has(parentKey)) {
          mobilePanelStack.push({ key: parentKey, trigger: null, scrollTop: 0 });
        }
      }

      if (key !== "root" && mobilePanelMap.has(key)) {
        mobilePanelStack.push({ key, trigger, scrollTop: 0 });
      }

      renderMobilePanels({ focus: true, restoreScroll: false });
    }

    function handleMobileDrill(event) {
      const targetKey = event.currentTarget.dataset.hzHeaderMobileTarget;
      if (!targetKey) return;
      openMobilePanel(targetKey, event.currentTarget);
    }

    function handleMobileBack() {
      if (mobilePanelStack.length <= 1) return;

      const leavingEntry = mobilePanelStack.pop();
      const leavingPanel = mobilePanelMap.get(leavingEntry.key);
      if (leavingPanel) leavingEntry.scrollTop = leavingPanel.scrollTop;

      const returnTrigger = leavingEntry.trigger;
      renderMobilePanels({ focus: false });

      window.requestAnimationFrame(() => {
        returnTrigger?.focus({ preventScroll: true });
      });
    }

    function handleNavigationMediaChange() {
      clearAllNavigationTimers();
      closeAllNavItems();
      resetMobilePanels();
    }

    function handleNavigationBlockSelect(event) {
      const blockId = event.detail?.blockId;
      if (!blockId) return;

      const matchingElements = Array.from(
        root.querySelectorAll("[data-hz-header-menu-block-id]")
      ).filter((element) => element.dataset.hzHeaderMenuBlockId === blockId);

      if (desktopNavMedia.matches) {
        const item = matchingElements
          .map((element) =>
            element.matches("[data-hz-header-nav-item]")
              ? element
              : element.closest("[data-hz-header-nav-item]")
          )
          .find(Boolean);

        if (item) {
          openNavItem(item);
        }
      } else {
        const mobileElement = matchingElements.find((element) =>
          element.closest("[data-hz-header-mobile-nav-panel]")
        );
        const mobilePanel = mobileElement?.closest(
          "[data-hz-header-mobile-nav-panel]"
        );
        const rootDrill = mobileElement?.querySelector?.(
          "[data-hz-header-mobile-drill]"
        );
        const panelKey =
          rootDrill?.dataset.hzHeaderMobileTarget ||
          mobilePanel?.dataset.hzHeaderMobilePanelKey;

        if (panelKey) {
          openDrawer();
          openMobilePanelPath(panelKey, rootDrill || null);
        }
      }
    }

    function handleNavigationBlockDeselect(event) {
      const blockId = event.detail?.blockId;
      if (!blockId || !desktopNavMedia.matches) return;

      const matchingElement = Array.from(
        root.querySelectorAll("[data-hz-header-menu-block-id]")
      ).find((element) => element.dataset.hzHeaderMenuBlockId === blockId);
      const item = matchingElement?.matches("[data-hz-header-nav-item]")
        ? matchingElement
        : matchingElement?.closest("[data-hz-header-nav-item]");

      if (item) {
        closeNavItem(item);
      }
    }

    function initializeNavigation() {
      navItems.forEach((item) => {
        const panel = getNavPanel(item);
        const toggle = getNavToggle(item);

        item.removeAttribute("data-hz-header-nav-open");
        toggle?.setAttribute("aria-expanded", "false");
        setPanelA11yState(panel, false);

        item.addEventListener("pointerenter", handleNavPointerEnter, {
          signal: navAbortController.signal
        });
        item.addEventListener("pointerleave", handleNavPointerLeave, {
          signal: navAbortController.signal
        });
        item.addEventListener("focusin", handleNavFocusIn, {
          signal: navAbortController.signal
        });
        item.addEventListener("focusout", handleNavFocusOut, {
          signal: navAbortController.signal
        });
        item.addEventListener("keydown", handleNavKeydown, {
          signal: navAbortController.signal
        });
        toggle?.addEventListener("click", handleNavToggle, {
          signal: navAbortController.signal
        });
      });

      flyoutItems.forEach((item) => {
        const panel = getFlyoutPanel(item);
        const toggle = getFlyoutToggle(item);

        item.removeAttribute("data-hz-header-flyout-open");
        toggle?.setAttribute("aria-expanded", "false");
        setPanelA11yState(panel, false);

        item.addEventListener("pointerenter", handleFlyoutPointerEnter, {
          signal: navAbortController.signal
        });
        item.addEventListener("pointerleave", handleFlyoutPointerLeave, {
          signal: navAbortController.signal
        });
        item.addEventListener("focusin", handleFlyoutFocusIn, {
          signal: navAbortController.signal
        });
        item.addEventListener("focusout", handleFlyoutFocusOut, {
          signal: navAbortController.signal
        });
        item.addEventListener("keydown", handleFlyoutKeydown, {
          signal: navAbortController.signal
        });
        toggle?.addEventListener("click", handleFlyoutToggle, {
          signal: navAbortController.signal
        });
      });

      mobileDrillButtons.forEach((button) => {
        button.addEventListener("click", handleMobileDrill, {
          signal: navAbortController.signal
        });
      });

      mobileBackButtons.forEach((button) => {
        button.addEventListener("click", handleMobileBack, {
          signal: navAbortController.signal
        });
      });

      resetMobilePanels();

      document.addEventListener("pointerdown", handleDocumentPointerDown, {
        signal: navAbortController.signal
      });
      root.addEventListener("shopify:block:select", handleNavigationBlockSelect, {
        signal: navAbortController.signal
      });
      root.addEventListener(
        "shopify:block:deselect",
        handleNavigationBlockDeselect,
        { signal: navAbortController.signal }
      );
      desktopNavMedia.addEventListener("change", handleNavigationMediaChange);
    }

    function hasOpenHeaderInteraction() {
      return Boolean(
        drawer?.open ||
          searchElement?.hasAttribute("open") ||
          accountSheetOpen ||
          root.querySelector('[data-hz-header-nav-open="true"]') ||
          root.matches(":focus-within")
      );
    }

    function setScrolledState() {
      const scrollY = Math.max(0, window.scrollY);
      root.classList.toggle("hz__headerWrap--scrolled", scrollY > 8);
      const delta = scrollY - previousScrollY;

      if (delta > 0) {
        downwardTravel += delta;
        upwardTravel = 0;
      } else if (delta < 0) {
        upwardTravel += Math.abs(delta);
        downwardTravel = 0;
      }

      if (stickyMode !== "reveal") {
        setHeaderHidden(false);
      } else if (scrollY <= 12 || hasOpenHeaderInteraction()) {
        setHeaderHidden(false);
        downwardTravel = 0;
        upwardTravel = 0;
      } else {
        const headerHeight =
          headerElement?.getBoundingClientRect().height || root.offsetHeight || 0;
        const hideThreshold = Math.max(
          28,
          Math.min(72, headerHeight * 0.5)
        );
        // Ignore small upward deltas caused by the header's own height reduction.
        const showThreshold = Math.max(
          28,
          Math.min(40, headerHeight * 0.25)
        );

        if (
          delta > 0 &&
          scrollY > headerHeight + 12 &&
          downwardTravel >= hideThreshold
        ) {
          setHeaderHidden(true);
          downwardTravel = 0;
        } else if (delta < 0 && upwardTravel >= showThreshold) {
          setHeaderHidden(false);
          upwardTravel = 0;
        }
      }

      previousScrollY = scrollY;
    }

    function scheduleScrollState() {
      if (rafId !== null) return;

      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        setScrolledState();
      });
    }

    function openDrawer() {
      if (!drawer || drawer.open) return;

      closeAllNavItems();
      setHeaderHidden(false);

      if (drawerCloseTimer) {
        window.clearTimeout(drawerCloseTimer);
        drawerCloseTimer = null;
      }

      drawer.showModal();

      window.requestAnimationFrame(() => {
        drawer.classList.add("hz__headerDrawer--visible");
      });
    }

    function finalizeDrawerClose() {
      if (!drawer) return;

      if (drawer.open) {
        drawer.close();
      }

      drawer.classList.remove("hz__headerDrawer--visible");
      resetMobilePanels();
    }

    function closeDrawer() {
      if (!drawer?.open) return;

      drawer.classList.remove("hz__headerDrawer--visible");

      if (drawerCloseTimer) {
        window.clearTimeout(drawerCloseTimer);
      }

      drawerCloseTimer = window.setTimeout(
        finalizeDrawerClose,
        hzHeaderReducedMotion.matches ? 0 : 380
      );
    }

    function handleDrawerCancel(event) {
      event.preventDefault();
      closeDrawer();
    }

    function handleDrawerBackdrop(event) {
      if (event.target === drawer) {
        closeDrawer();
      }
    }

    function syncSearchContent() {
      if (!searchInput) return;

      const hasQuery = searchInput.value.trim().length > 0;

      if (searchDefault) {
        searchDefault.hidden = hasQuery;
      }

      if (searchResults) {
        searchResults.hidden = !hasQuery;
      }
    }

    function handleSearchInput() {
      syncSearchContent();
    }

    function handleSearchBeforeShow() {
      closeAllNavItems();
      setHeaderHidden(false);
    }

    function handleSearchAfterShow() {
      setHeaderHidden(false);
      syncSearchContent();

      window.requestAnimationFrame(() => {
        searchInput?.focus({ preventScroll: true });
      });
    }

    function handleSearchAfterHide() {
      syncSearchContent();
    }

    function handleHeaderFocusIn() {
      setHeaderHidden(false);
    }

    function handleAccountOpen() {
      closeAllNavItems();
      accountSheetOpen = true;
      root.classList.add("hz__headerWrap--accountOpen");
      setHeaderHidden(false);
    }

    function handleAccountClose() {
      accountSheetOpen = false;
      root.classList.remove("hz__headerWrap--accountOpen");
    }

    function updateCartBadge(count) {
      const safeCount = Math.max(0, Number(count) || 0);

      cartCounts.forEach((element) => {
        element.textContent = safeCount > 99 ? "99+" : String(safeCount);
      });

      cartBadges.forEach((badge) => {
        badge.classList.toggle(
          "hz__headerCartBadge--hidden",
          safeCount <= 0
        );
      });
    }

    function handleCartChange(event) {
      const cart =
        event.detail?.cart ||
        event.detail?.detail?.cart ||
        null;

      const count =
        cart?.item_count ??
        cart?.totalQuantity ??
        cart?.total_quantity;

      if (count !== undefined) {
        updateCartBadge(count);
      }
    }

    async function handleCartRefresh() {
      try {
        const response = await fetch(
          `${window.Shopify?.routes?.root || "/"}cart.js`,
          {
            headers: {
              Accept: "application/json"
            }
          }
        );

        if (!response.ok) return;

        const cart = await response.json();
        updateCartBadge(cart.item_count);
      } catch (_) {}
    }

    function handleShopifyCartLinesUpdate(event) {
      const promise = event.promise;

      if (!promise || typeof promise.then !== "function") return;

      promise
        .then((payload) => {
          const count =
            payload?.cart?.totalQuantity ??
            payload?.detail?.cart?.item_count;

          if (count !== undefined) {
            updateCartBadge(count);
          }
        })
        .catch(() => {});
    }

    function handleResize() {
      if (window.innerWidth >= 1024 && drawer?.open) {
        finalizeDrawerClose();
      }

      if (desktopNavMedia.matches) {
        navItems.forEach((item) => {
          if (item.dataset.hzHeaderNavOpen === "true") {
            positionDropdown(item);
          }
        });

        flyoutItems.forEach((item) => {
          if (item.dataset.hzHeaderFlyoutOpen === "true") {
            positionFlyout(item);
          }
        });
      }

      syncThemeHeaderMetrics();
    }

    window.addEventListener("scroll", scheduleScrollState, {
      passive: true
    });
    window.addEventListener("resize", handleResize);

    root.addEventListener("focusin", handleHeaderFocusIn);
    accountElements.forEach((accountElement) => {
      accountElement.addEventListener("open", handleAccountOpen);
      accountElement.addEventListener("close", handleAccountClose);
    });
    openMenuButton?.addEventListener("click", openDrawer);
    closeMenuButton?.addEventListener("click", closeDrawer);
    drawer?.addEventListener("cancel", handleDrawerCancel);
    drawer?.addEventListener("click", handleDrawerBackdrop);

    searchInput?.addEventListener("input", handleSearchInput);
    searchInput?.addEventListener("search", handleSearchInput);
    searchElement?.addEventListener("dialog:before-show", handleSearchBeforeShow);
    searchElement?.addEventListener("dialog:after-show", handleSearchAfterShow);
    searchElement?.addEventListener("dialog:after-hide", handleSearchAfterHide);

    document.addEventListener("cart:change", handleCartChange);
    document.addEventListener("cart:refresh", handleCartRefresh);
    document.addEventListener(
      "shopify:cart:lines-update",
      handleShopifyCartLinesUpdate
    );

    if ("ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(syncThemeHeaderMetrics);
      resizeObserver.observe(headerElement || root);
    }

    initializeNavigation();
    setScrolledState();
    syncThemeHeaderMetrics();
    syncSearchContent();

    hzHeaderControllers.set(root, {
      destroy() {
        if (rafId !== null) {
          window.cancelAnimationFrame(rafId);
          rafId = null;
        }

        if (drawerCloseTimer) {
          window.clearTimeout(drawerCloseTimer);
          drawerCloseTimer = null;
        }

        resizeObserver?.disconnect();
        resizeObserver = null;

        clearAllNavigationTimers();
        closeAllNavItems();
        navAbortController.abort();
        desktopNavMedia.removeEventListener("change", handleNavigationMediaChange);
        root.classList.remove("hz__headerNavEnhanced");

        window.removeEventListener("scroll", scheduleScrollState);
        window.removeEventListener("resize", handleResize);
        root.removeEventListener("focusin", handleHeaderFocusIn);
        accountElements.forEach((accountElement) => {
          accountElement.removeEventListener("open", handleAccountOpen);
          accountElement.removeEventListener("close", handleAccountClose);
        });
        openMenuButton?.removeEventListener("click", openDrawer);
        closeMenuButton?.removeEventListener("click", closeDrawer);
        drawer?.removeEventListener("cancel", handleDrawerCancel);
        drawer?.removeEventListener("click", handleDrawerBackdrop);

        searchInput?.removeEventListener("input", handleSearchInput);
        searchInput?.removeEventListener("search", handleSearchInput);
        searchElement?.removeEventListener("dialog:before-show", handleSearchBeforeShow);
        searchElement?.removeEventListener("dialog:after-show", handleSearchAfterShow);
        searchElement?.removeEventListener("dialog:after-hide", handleSearchAfterHide);

        document.removeEventListener("cart:change", handleCartChange);
        document.removeEventListener("cart:refresh", handleCartRefresh);
        document.removeEventListener(
          "shopify:cart:lines-update",
          handleShopifyCartLinesUpdate
        );

        if (drawer?.open) {
          drawer.close();
        }

        root.classList.remove(
          "hz__headerWrap--hidden",
          "hz__headerWrap--scrolled",
          "hz__headerWrap--accountOpen"
        );
        sectionElement?.classList.remove(
          "hz__headerSection--sticky",
          "hz__headerSection--reveal",
          "hz__headerSection--hidden"
        );

        previousThemeHeaderVariables.forEach((value, property) => {
          if (value) {
            documentRootStyle.setProperty(property, value);
          } else {
            documentRootStyle.removeProperty(property);
          }
        });

        hzHeaderControllers.delete(root);
      }
    });
  }

  function hzHeaderInitWithin(scope = document) {
    if (scope.matches?.("[data-hz-header]")) {
      hzHeaderCreate(scope);
    }

    scope
      .querySelectorAll?.("[data-hz-header]")
      .forEach(hzHeaderCreate);
  }

  function hzHeaderDestroyWithin(scope) {
    const roots = [];

    if (scope.matches?.("[data-hz-header]")) {
      roots.push(scope);
    }

    scope
      .querySelectorAll?.("[data-hz-header]")
      .forEach((root) => roots.push(root));

    roots.forEach((root) => {
      hzHeaderControllers.get(root)?.destroy();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => hzHeaderInitWithin(document),
      { once: true }
    );
  } else {
    hzHeaderInitWithin(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    hzHeaderInitWithin(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    hzHeaderDestroyWithin(event.target);
  });
})();


/* --------------------------------------------------------------------------
   Shopify account signed-in avatar size
   Shopify internally clamps the signed-in avatar, so apply the visual size
   inside the account component's Shadow DOM.
   -------------------------------------------------------------------------- */

(() => {
  "use strict";

  const STYLE_ID = "hz-account-avatar-size-fix";
  const ACCOUNT_SELECTOR = "shopify-account.hz__headerAccount";

  function installAvatarFix(account, attempt = 0) {
    if (!account?.isConnected) return;

    const shadowRoot = account.shadowRoot;

    /*
     * Shopify may upgrade/render the custom element asynchronously.
     * Wait briefly for its Shadow DOM rather than racing initialization.
     */
    if (!shadowRoot) {
      if (attempt < 120) {
        window.requestAnimationFrame(() => {
          installAvatarFix(account, attempt + 1);
        });
      }

      return;
    }

    /*
     * Multiple header instances exist for desktop/mobile.
     * Do not inject the override more than once per component.
     */
    if (shadowRoot.getElementById(STYLE_ID)) return;

    const style = document.createElement("style");

    style.id = STYLE_ID;

    style.textContent = `
      .account-button__avatar {
        --clamped-avatar-size: 21px !important;

        width: 21px !important;
        min-width: 21px !important;
        max-width: 21px !important;

        height: 21px !important;
        min-height: 21px !important;
        max-height: 21px !important;

        flex-basis: 21px !important;
      }

      .account-button__avatar img {
        width: 100% !important;
        height: 100% !important;
        object-fit: cover !important;
      }
    `;

    shadowRoot.append(style);
  }

  function init(scope = document) {
    if (scope.matches?.(ACCOUNT_SELECTOR)) {
      installAvatarFix(scope);
    }

    scope
      .querySelectorAll?.(ACCOUNT_SELECTOR)
      .forEach(installAvatarFix);
  }

  customElements
    .whenDefined("shopify-account")
    .then(() => init(document));

  /*
   * Re-run when Shopify reloads the Header section in the Theme Editor.
   */
  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });
})();
/* --------------------------------------------------------------------------
   AR Contact Page
   Shopify's native contact form remains authoritative. This controller adds
   accessible validation feedback, a submit busy state, and double-submit
   protection without intercepting or replacing the native submission flow.
   -------------------------------------------------------------------------- */

(() => {
  "use strict";

  const hzContactPageControllers = new WeakMap();

  function hzContactPageCreate(form) {
    if (!form || hzContactPageControllers.has(form)) return;

    const submit = form.querySelector("[data-hz-contact-submit]");
    const clientStatus = form.querySelector("[data-hz-contact-client-status]");
    const fields = Array.from(form.querySelectorAll("[data-hz-contact-field]"));

    if (!submit || !clientStatus || fields.length === 0) return;

    const clientStatusId = clientStatus.id;
    const validationMessage =
      form.dataset.hzContactInvalidMessage ||
      "Please check the highlighted fields and try again.";

    let invalidFrameId = null;
    let hasAttemptedValidation = false;

    function addDescribedBy(field, id) {
      if (!field || !id) return;

      const ids = new Set(
        (field.getAttribute("aria-describedby") || "")
          .split(/\s+/)
          .filter(Boolean)
      );

      ids.add(id);
      field.setAttribute("aria-describedby", Array.from(ids).join(" "));
    }

    function removeDescribedBy(field, id) {
      if (!field || !id) return;

      const ids = (field.getAttribute("aria-describedby") || "")
        .split(/\s+/)
        .filter((value) => value && value !== id);

      if (ids.length > 0) {
        field.setAttribute("aria-describedby", ids.join(" "));
      } else {
        field.removeAttribute("aria-describedby");
      }
    }

    function setLoading(isLoading) {
      submit.disabled = isLoading;

      if (isLoading) {
        submit.setAttribute("aria-busy", "true");
        form.setAttribute("aria-busy", "true");
      } else {
        submit.removeAttribute("aria-busy");
        form.removeAttribute("aria-busy");
      }
    }

    function hideClientStatus() {
      clientStatus.hidden = true;
      clientStatus.textContent = "";
    }

    function showClientStatus() {
      clientStatus.textContent = validationMessage;
      clientStatus.hidden = false;
    }

    function refreshInvalidState() {
      invalidFrameId = null;

      if (!hasAttemptedValidation) {
        hideClientStatus();
        return;
      }

      const invalidFields = fields.filter((field) => !field.validity.valid);
      const invalidSet = new Set(invalidFields);

      fields.forEach((field) => {
        if (invalidSet.has(field)) {
          field.setAttribute("aria-invalid", "true");
          addDescribedBy(field, clientStatusId);
        } else {
          if (!field.matches("[data-hz-contact-server-invalid]")) {
            field.removeAttribute("aria-invalid");
          }
          removeDescribedBy(field, clientStatusId);
        }
      });

      if (invalidFields.length > 0) {
        showClientStatus();
      } else {
        hideClientStatus();
      }
    }

    function scheduleInvalidRefresh() {
      if (invalidFrameId !== null) {
        window.cancelAnimationFrame(invalidFrameId);
      }

      invalidFrameId = window.requestAnimationFrame(refreshInvalidState);
    }

    function handleInvalid(event) {
      const field = event.target;
      if (!fields.includes(field)) return;

      hasAttemptedValidation = true;
      field.setAttribute("aria-invalid", "true");
      addDescribedBy(field, clientStatusId);
      scheduleInvalidRefresh();
    }

    function handleFieldInput(event) {
      const field = event.currentTarget;

      if (field.validity.valid) {
        field.removeAttribute("aria-invalid");
        field.removeAttribute("data-hz-contact-server-invalid");
        removeDescribedBy(field, clientStatusId);
      }

      if (hasAttemptedValidation) {
        scheduleInvalidRefresh();
      }
    }

    function handleSubmit() {
      /*
       * A normal `submit` event fires only after native constraint validation
       * succeeds. Do not preventDefault: Shopify/Prestige/CAPTCHA retain full
       * ownership of the request, response, and anti-spam workflow.
       */
      hasAttemptedValidation = false;
      hideClientStatus();
      setLoading(true);
    }

    function resetTransientState() {
      setLoading(false);
    }

    form.addEventListener("invalid", handleInvalid, true);
    form.addEventListener("submit", handleSubmit);
    fields.forEach((field) => {
      field.addEventListener("input", handleFieldInput);
      field.addEventListener("change", handleFieldInput);
    });

    hzContactPageControllers.set(form, {
      destroy() {
        form.removeEventListener("invalid", handleInvalid, true);
        form.removeEventListener("submit", handleSubmit);
        fields.forEach((field) => {
          field.removeEventListener("input", handleFieldInput);
          field.removeEventListener("change", handleFieldInput);
        });

        if (invalidFrameId !== null) {
          window.cancelAnimationFrame(invalidFrameId);
          invalidFrameId = null;
        }

        resetTransientState();
        hzContactPageControllers.delete(form);
      },
      reset: resetTransientState
    });
  }

  function hzContactPageInitWithin(scope = document) {
    if (scope.matches?.("[data-hz-contact-page-form]")) {
      hzContactPageCreate(scope);
    }

    scope
      .querySelectorAll?.("[data-hz-contact-page-form]")
      .forEach(hzContactPageCreate);
  }

  function hzContactPageDestroyWithin(scope) {
    const forms = [];

    if (scope.matches?.("[data-hz-contact-page-form]")) {
      forms.push(scope);
    }

    scope
      .querySelectorAll?.("[data-hz-contact-page-form]")
      .forEach((form) => forms.push(form));

    forms.forEach((form) => {
      hzContactPageControllers.get(form)?.destroy();
    });
  }

  function hzContactPageResetAll() {
    document
      .querySelectorAll("[data-hz-contact-page-form]")
      .forEach((form) => {
        hzContactPageControllers.get(form)?.reset();
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => hzContactPageInitWithin(document),
      { once: true }
    );
  } else {
    hzContactPageInitWithin(document);
  }

  window.addEventListener("pageshow", hzContactPageResetAll);

  document.addEventListener("shopify:section:load", (event) => {
    hzContactPageInitWithin(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    hzContactPageDestroyWithin(event.target);
  });
})();

/* --------------------------------------------------------------------------
   AR FAQ Page
   Page-specific accordion controller. Intentionally independent from the
   existing AR FAQ Image HP section so multiple FAQ systems cannot collide.
   -------------------------------------------------------------------------- */

(() => {
  "use strict";

  const hzFaqPageControllers = new WeakMap();
  const hzFaqPageHeightTimers = new WeakMap();

  function hzFaqPageCreate(root) {
    if (!root || hzFaqPageControllers.has(root)) return;

    const mode = root.dataset.hzFaqPageMode === "multiple" ? "multiple" : "single";
    const items = Array.from(root.querySelectorAll("[data-hz-faq-page-item]"));

    if (items.length === 0) return;

    function getParts(item) {
      return {
        trigger: item.querySelector("[data-hz-faq-page-trigger]"),
        answer: item.querySelector("[data-hz-faq-page-answer]")
      };
    }

    function clearHeightTimer(answer) {
      const timer = hzFaqPageHeightTimers.get(answer);
      if (timer !== undefined) {
        window.clearTimeout(timer);
        hzFaqPageHeightTimers.delete(answer);
      }
    }

    function getHeightTransitionMs(answer) {
      const styles = window.getComputedStyle(answer);
      const durations = styles.transitionDuration.split(",");
      const delays = styles.transitionDelay.split(",");

      const toMs = (value) => {
        const trimmed = value.trim();
        if (trimmed.endsWith("ms")) return Number.parseFloat(trimmed) || 0;
        if (trimmed.endsWith("s")) return (Number.parseFloat(trimmed) || 0) * 1000;
        return 0;
      };

      return toMs(durations[0] || "0s") + toMs(delays[0] || "0s");
    }

    function scheduleAutoHeight(answer, item) {
      clearHeightTimer(answer);

      const delay = getHeightTransitionMs(answer) + 50;
      const timer = window.setTimeout(() => {
        if (item.dataset.open === "true") {
          answer.style.height = "auto";
        }
        hzFaqPageHeightTimers.delete(answer);
      }, delay);

      hzFaqPageHeightTimers.set(answer, timer);
    }

    function animateAnswer(item, open) {
      const { answer } = getParts(item);
      if (!answer) return;

      clearHeightTimer(answer);

      if (open) {
        const currentHeight = answer.getBoundingClientRect().height;
        answer.style.height = `${currentHeight}px`;

        window.requestAnimationFrame(() => {
          if (item.dataset.open !== "true") return;
          answer.style.height = `${answer.scrollHeight}px`;
          scheduleAutoHeight(answer, item);
        });
      } else {
        const currentHeight = answer.getBoundingClientRect().height;
        answer.style.height = `${currentHeight}px`;

        /* Force the explicit starting height to be committed before collapse. */
        void answer.offsetHeight;

        window.requestAnimationFrame(() => {
          if (item.dataset.open === "true") return;
          answer.style.height = "0px";
        });
      }
    }

    function setItemState(item, open, { animate = true } = {}) {
      const { trigger, answer } = getParts(item);
      if (!trigger || !answer) return;

      item.dataset.open = open ? "true" : "false";
      trigger.setAttribute("aria-expanded", open ? "true" : "false");
      answer.setAttribute("aria-hidden", open ? "false" : "true");

      if (open) {
        answer.removeAttribute("inert");
      } else {
        answer.setAttribute("inert", "");
      }

      if (!animate || root.dataset.hzFaqPageEnhanced !== "true") {
        clearHeightTimer(answer);
        answer.style.height = open ? "auto" : "0px";
        return;
      }

      animateAnswer(item, open);
    }

    function openItem(targetItem) {
      if (mode === "single") {
        items.forEach((item) => {
          if (item !== targetItem && item.dataset.open === "true") {
            setItemState(item, false);
          }
        });
      }

      setItemState(targetItem, true);
    }

    function toggleItem(item) {
      const shouldOpen = item.dataset.open !== "true";

      if (shouldOpen && mode === "single") {
        items.forEach((candidate) => {
          if (candidate !== item && candidate.dataset.open === "true") {
            setItemState(candidate, false);
          }
        });
      }

      setItemState(item, shouldOpen);
    }

    function normalizeInitialState() {
      const initiallyOpen = items.filter((item) => item.dataset.open === "true");

      if (mode === "single" && initiallyOpen.length > 1) {
        initiallyOpen.slice(1).forEach((item) => {
          item.dataset.open = "false";
        });
      }

      items.forEach((item) => {
        setItemState(item, item.dataset.open === "true", { animate: false });
      });

      root.dataset.hzFaqPageEnhanced = "true";
    }

    function handleClick(event) {
      const trigger = event.target.closest("[data-hz-faq-page-trigger]");
      if (!trigger || !root.contains(trigger)) return;

      const item = trigger.closest("[data-hz-faq-page-item]");
      if (!item || !root.contains(item)) return;

      toggleItem(item);
    }

    function handleKeydown(event) {
      const trigger = event.target.closest("[data-hz-faq-page-trigger]");
      if (!trigger || !root.contains(trigger)) return;

      const triggers = items
        .map((item) => item.querySelector("[data-hz-faq-page-trigger]"))
        .filter(Boolean);

      const currentIndex = triggers.indexOf(trigger);
      if (currentIndex < 0) return;

      let nextIndex = null;

      if (event.key === "ArrowDown") {
        nextIndex = (currentIndex + 1) % triggers.length;
      } else if (event.key === "ArrowUp") {
        nextIndex = (currentIndex - 1 + triggers.length) % triggers.length;
      } else if (event.key === "Home") {
        nextIndex = 0;
      } else if (event.key === "End") {
        nextIndex = triggers.length - 1;
      }

      if (nextIndex === null) return;

      event.preventDefault();
      triggers[nextIndex].focus();
    }

    function handleTransitionEnd(event) {
      const answer = event.target.closest?.("[data-hz-faq-page-answer]");
      if (!answer || !root.contains(answer) || event.propertyName !== "height") return;

      const item = answer.closest("[data-hz-faq-page-item]");
      if (!item) return;

      clearHeightTimer(answer);

      if (item.dataset.open === "true") {
        answer.style.height = "auto";
      } else {
        answer.style.height = "0px";
      }
    }

    function handleBlockSelect(event) {
      const selected = event.target.closest?.("[data-hz-faq-page-block-id]");
      if (!selected || !root.contains(selected)) return;

      openItem(selected);

      if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        window.requestAnimationFrame(() => {
          selected.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
      }
    }

    function destroy() {
      root.removeEventListener("click", handleClick);
      root.removeEventListener("keydown", handleKeydown);
      root.removeEventListener("transitionend", handleTransitionEnd);
      document.removeEventListener("shopify:block:select", handleBlockSelect);

      items.forEach((item) => {
        const { trigger, answer } = getParts(item);
        if (!answer) return;

        clearHeightTimer(answer);
        answer.removeAttribute("inert");
        answer.removeAttribute("style");
        answer.setAttribute("aria-hidden", "false");
        trigger?.setAttribute("aria-expanded", "true");
      });

      delete root.dataset.hzFaqPageEnhanced;
      hzFaqPageControllers.delete(root);
    }

    normalizeInitialState();

    root.addEventListener("click", handleClick);
    root.addEventListener("keydown", handleKeydown);
    root.addEventListener("transitionend", handleTransitionEnd);
    document.addEventListener("shopify:block:select", handleBlockSelect);

    hzFaqPageControllers.set(root, { destroy });
  }

  function hzFaqPageInitWithin(scope = document) {
    if (scope.matches?.("[data-hz-faq-page]")) {
      hzFaqPageCreate(scope);
    }

    scope.querySelectorAll?.("[data-hz-faq-page]").forEach(hzFaqPageCreate);
  }

  function hzFaqPageDestroyWithin(scope) {
    const roots = [];

    if (scope.matches?.("[data-hz-faq-page]")) {
      roots.push(scope);
    }

    scope.querySelectorAll?.("[data-hz-faq-page]").forEach((root) => roots.push(root));

    roots.forEach((root) => hzFaqPageControllers.get(root)?.destroy());
  }

  /* arScripts.js is loaded after the storefront markup, so initialize now to
     minimize any no-JS fallback flash while preserving progressive fallback. */
  hzFaqPageInitWithin(document);

  document.addEventListener("shopify:section:load", (event) => {
    hzFaqPageInitWithin(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    hzFaqPageDestroyWithin(event.target);
  });
})();